/**
 * Marketing — funnel + marketing-spend dashboard, plus Reviews assignment.
 *
 * Two tabs:
 *   "Funnel"  — request → quote → job → invoice flow with QB ad spend.
 *   "Reviews" — every Google/Jobber review with manual client→job→tech
 *               assignment. Replaces the unreliable AI matcher that used to
 *               run from CrewPerformance.
 *
 * Funnel data flow:
 *   /api/marketing?action=filters         → dropdown options
 *   /api/marketing?action=funnel&...      → KPI strip + cost-per-stage + spend
 *   /api/marketing?action=table&...       → detail rows
 *   /api/jobber-sync?days=N               → live re-pull of quotes/requests/jobs
 *
 * Reviews data flow: direct Supabase reads of jobber_reviews / jb_jobs /
 * jobber_timesheets. Saving an assignment writes assigned_client_id +
 * assigned_job + assigned_techs to jobber_reviews, which CrewPerformance
 * already prefers over auto_assigned_techs in its leaderboard logic.
 *
 * Tech credit when a client is manually picked:
 *   - 1 job for that client            → that job's techs
 *   - Multi-job, rating ≥ 4            → union of techs across ALL of the
 *                                         client's jobs (good review benefits
 *                                         everyone who's worked for them)
 *   - Multi-job, rating ≤ 3            → only the techs on the job closest
 *                                         to the review date (bad review
 *                                         points the finger at the recent job)
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { supabase } from "../lib/supabase";
import { usePermissions } from "../hooks/usePermissions";
import { apiFetch } from "../lib/apiFetch";

const API = "/api/marketing";

const DATE_BASIS_OPTIONS = [
  { value: "any",             label: "Any stage in window" },
  { value: "request_created", label: "Request received" },
  { value: "quote_drafted",   label: "Quote drafted" },
  { value: "quote_sent",      label: "Quote sent" },
  { value: "quote_approved",  label: "Quote approved" },
  { value: "job_created",     label: "Job created" },
  { value: "job_closed",      label: "Job closed" },
  { value: "invoice_issued",  label: "Invoice issued" },
];

// Last 30 days as the default window
function defaultRange() {
  const end = new Date();
  const start = new Date(end.getTime() - 30 * 24 * 60 * 60 * 1000);
  return {
    start: start.toISOString().slice(0, 10),
    end:   end.toISOString().slice(0, 10),
  };
}

const fmtMoney = (n) => {
  const v = Number(n || 0);
  if (!isFinite(v)) return "—";
  return v.toLocaleString("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 });
};
const fmtMoney2 = (n) => {
  const v = Number(n || 0);
  if (!isFinite(v)) return "—";
  return v.toLocaleString("en-CA", { style: "currency", currency: "CAD" });
};
const fmtDate = (d) => d ? String(d).slice(0, 10) : "—";
const fmtPct  = (n) => (n == null || isNaN(n)) ? "—" : `${n}%`;
const fmtDays = (n) => (n == null) ? "—" : `${n}d`;

// ─────────────────────────────────────────────────────────────────────────
export default function Marketing() {
  const perms = usePermissions() || {};
  const canViewMarketing = !!(
    perms.isAdmin || perms.isOwner || perms.hasPermission?.("view_marketing")
  );
  if (!canViewMarketing) return <MarketingAccessDenied />;

  return <MarketingInner />;
}

function MarketingAccessDenied() {
  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={S.cardTitle}>Marketing access required</div>
        <div style={{ ...S.cardSubtitle, marginTop: 8, lineHeight: 1.5 }}>
          Ask your administrator for the <code style={{ color: "#a5ce3a" }}>view_marketing</code> permission
          to see this page. Revenue, profit, and ROAS additionally require
          <code style={{ color: "#a5ce3a" }}> view_marketing_finance</code>.
        </div>
      </div>
    </div>
  );
}

// The actual Marketing tab body — only mounted once the permission gate passes.
function MarketingInner() {
  const [activeTab, setActiveTab]         = useState("overview"); // "overview" | "funnel" | "reviews"
  const [range, setRange]                 = useState(defaultRange());
  const [serviceType, setServiceType]     = useState("all");
  const [leadSource, setLeadSource]       = useState("all");
  const [keyword, setKeyword]             = useState("");
  const [dateBasis, setDateBasis]         = useState("any");

  const [serviceTypes, setServiceTypes]   = useState([]);
  const [leadSources, setLeadSources]     = useState([]);

  const [funnel, setFunnel]               = useState(null);
  const [tableData, setTableData]         = useState(null);

  const [busy, setBusy]                   = useState("");
  const [error, setError]                 = useState("");
  const [status, setStatus]               = useState("");

  const [sortKey, setSortKey]             = useState("invoice_issued");
  const [sortDir, setSortDir]             = useState("desc");
  const [shownColumns, setShownColumns]   = useState({
    quote_num: true,  job_num: true, invoice_num: false,
    client_name: true, title: true, service_type: true, lead_source: true,
    status: false, drafted_date: false, sent_date: true,
    approved_date: true, archived_date: false, converted_date: false,
    job_created: false, job_closed: true, invoice_issued: true,
    quote_total: true, job_revenue: true, job_profit: true, job_profit_pct: true,
    days_request_to_invoice: true,
  });

  // Load filter options on mount
  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch(`${API}?action=filters`).then(r => r.json());
        setServiceTypes(r.service_types || []);
        setLeadSources(r.lead_sources || []);
      } catch (e) { setError(e.message); }
    })();
  }, []);

  // Reload funnel + table whenever filters change (debounced for keyword)
  const loadData = useCallback(async () => {
    setBusy("loading"); setError("");
    try {
      const params = new URLSearchParams({
        start: range.start, end: range.end,
        date_basis: dateBasis,
        service_type: serviceType,
        lead_source: leadSource,
        keyword,
      });
      const [f, t] = await Promise.all([
        apiFetch(`${API}?action=funnel&${params}`).then(r => r.json()),
        apiFetch(`${API}?action=table&${params}`).then(r => r.json()),
      ]);
      if (!f.ok) throw new Error(f.error || "Funnel failed");
      if (!t.ok) throw new Error(t.error || "Table failed");
      setFunnel(f);
      setTableData(t);
    } catch (e) { setError(e.message); }
    finally { setBusy(""); }
  }, [range.start, range.end, dateBasis, serviceType, leadSource, keyword]);

  useEffect(() => {
    const h = setTimeout(loadData, keyword ? 350 : 0);
    return () => clearTimeout(h);
  }, [loadData, keyword]);

  // Trigger a live sync from Jobber. This calls /api/jobber-sync directly
  // (the marketing endpoint just suggests the URL — actual work happens in
  // the broader Jobber sync pipeline).
  const triggerSync = useCallback(async (days = 90) => {
    setBusy("syncing"); setStatus(""); setError("");
    try {
      const r = await apiFetch(`/api/jobber-sync?days=${days}`, { method: "POST" });
      const data = await r.json();
      if (!r.ok || data.error) throw new Error(data.error || `HTTP ${r.status}`);
      const { quotes, requests, jobs } = data.synced || {};
      setStatus(
        `Synced ${jobs?.upserted ?? "?"} jobs · ${quotes?.upserted ?? "?"} quotes · ${requests?.upserted ?? "?"} requests in ${data.timings?.quotes || "?"}.`
      );
      await loadData();
    } catch (e) { setError(`Sync failed: ${e.message}`); }
    finally { setBusy(""); }
  }, [loadData]);

  // Sort + slice for table display
  const sortedRows = useMemo(() => {
    if (!tableData?.rows) return [];
    const rows = [...tableData.rows];
    rows.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return sortDir === "asc" ? av - bv : bv - av;
      }
      return sortDir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return rows;
  }, [tableData, sortKey, sortDir]);

  // Footer aggregation on currently visible rows
  const footer = useMemo(() => {
    if (!sortedRows.length) return null;
    let quoteTotal = 0, jobRev = 0, jobProfit = 0;
    let qCount = 0, jCount = 0, invCount = 0;
    for (const r of sortedRows) {
      if (r.quote_total)  { quoteTotal += Number(r.quote_total); qCount += 1; }
      if (r.job_revenue)  { jobRev += Number(r.job_revenue); jCount += 1; }
      if (r.job_profit)   { jobProfit += Number(r.job_profit); }
      if (r.invoice_issued) invCount += 1;
    }
    return {
      rows: sortedRows.length,
      quote_total: quoteTotal, q_count: qCount,
      job_rev: jobRev,         j_count: jCount,
      job_profit: jobProfit,
      invoiced: invCount,
      profit_pct: jobRev > 0 ? Math.round((jobProfit / jobRev) * 1000) / 10 : null,
    };
  }, [sortedRows]);

  const toggleCol = (key) => setShownColumns(c => ({ ...c, [key]: !c[key] }));
  const sortBy = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  };
  const sortIndicator = (k) => sortKey === k ? (sortDir === "asc" ? " ↑" : " ↓") : "";

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Marketing & Funnel</div>
          <div style={S.subtitle}>
            {activeTab === "overview"
              ? "Top-level marketing metrics: spend, leads, qualification, revenue, and recommended actions."
              : activeTab === "takeover"
              ? "Full available SwiftSocial history and the staged generator landing page."
              : activeTab === "funnel"
              ? "Lead → Quote → Job → Invoice flow with QB digital advertising spend (account 6203)."
              : "All Google + Jobber reviews. Assign each one to a client so the leaderboard credits the right techs."}
          </div>
        </div>
        {activeTab === "funnel" && (
          <div style={S.headerActions}>
            <button onClick={() => triggerSync(90)} disabled={!!busy} style={S.btn}>
              {busy === "syncing" ? "Syncing…" : "Sync Jobber (90d)"}
            </button>
            <button onClick={() => triggerSync(365)} disabled={!!busy} style={S.btn}>Backfill 1y</button>
          </div>
        )}
      </div>

      {error && <div style={S.errorBanner}>{error}<button style={S.bannerClose} onClick={() => setError("")}>×</button></div>}
      {status && <div style={S.statusBanner}>{status}<button style={S.bannerClose} onClick={() => setStatus("")}>×</button></div>}

      {/* Tab strip */}
      <div style={S.tabStrip}>
        <button
          style={{ ...S.tab, ...(activeTab === "overview" ? S.tabActive : {}) }}
          onClick={() => setActiveTab("overview")}
        >
          Overview
        </button>
        <button
          style={{ ...S.tab, ...(activeTab === "googleads" ? S.tabActive : {}) }}
          onClick={() => setActiveTab("googleads")}
        >
          Google Ads
        </button>
        <button
          style={{ ...S.tab, ...(activeTab === "trends" ? S.tabActive : {}) }}
          onClick={() => setActiveTab("trends")}
        >
          Trends &amp; ROAS
        </button>
        <button
          style={{ ...S.tab, ...(activeTab === "takeover" ? S.tabActive : {}) }}
          onClick={() => setActiveTab("takeover")}
        >
          Swift takeover
        </button>
        <button
          style={{ ...S.tab, ...(activeTab === "adbuilder" ? S.tabActive : {}) }}
          onClick={() => setActiveTab("adbuilder")}
        >
          Ad Builder
        </button>
        <button
          style={{ ...S.tab, ...(activeTab === "control" ? S.tabActive : {}) }}
          onClick={() => setActiveTab("control")}
        >
          Control
        </button>
        <button
          style={{ ...S.tab, ...(activeTab === "funnel" ? S.tabActive : {}) }}
          onClick={() => setActiveTab("funnel")}
        >
          Funnel
        </button>
        <button
          style={{ ...S.tab, ...(activeTab === "reviews" ? S.tabActive : {}) }}
          onClick={() => setActiveTab("reviews")}
        >
          Reviews
        </button>
      </div>

      {activeTab === "overview"  && <OverviewTab />}
      {activeTab === "googleads" && <GoogleAdsTab />}
      {activeTab === "trends"    && <TrendsTab />}
      {activeTab === "takeover"  && <SwiftTakeoverTab />}
      {activeTab === "adbuilder" && <AdBuilderTab />}
      {activeTab === "control"   && <ControlTab />}
      {activeTab === "reviews"   && <ReviewsTab />}
      {activeTab === "funnel"   && (
        <FunnelTabContent
          range={range} setRange={setRange}
          dateBasis={dateBasis} setDateBasis={setDateBasis}
          serviceType={serviceType} setServiceType={setServiceType}
          serviceTypes={serviceTypes}
          leadSource={leadSource} setLeadSource={setLeadSource}
          leadSources={leadSources}
          keyword={keyword} setKeyword={setKeyword}
          loadData={loadData} busy={busy}
          funnel={funnel} tableData={tableData}
          sortedRows={sortedRows} shownColumns={shownColumns}
          toggleCol={toggleCol} sortBy={sortBy} sortIndicator={sortIndicator}
          footer={footer}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Overview tab — Marketing Command Center (Phase 1)
// ═══════════════════════════════════════════════════════════════════════════
//
// Top-level metrics + recommended action summary. Reads `/api/marketing
// ?action=overview` which rolls up marketing_daily_metrics + marketing_leads
// + marketing_recommendations. Falls back to QB ad spend when the new tables
// are empty, so the page works on day 1 before Meta is connected.
//
// Finance-sensitive metrics (spend, CPL, CPQL, revenue, GP, ROAS, GP ROAS)
// are hidden when the viewer lacks `view_marketing_finance`. Admins/owners
// pass that check automatically.

const FINANCE_METRIC_KEYS = new Set([
  "ad_spend", "cost_per_lead", "cost_per_qualified",
  "approved_revenue", "estimated_gp", "roas", "gp_roas",
]);

function fmtMetric(m) {
  if (m == null) return "—";
  const v = m.value;
  if (v == null || (typeof v === "number" && !isFinite(v))) return "—";
  switch (m.format) {
    case "money":      return fmtMoney(v);
    case "percent":    return `${v}%`;
    case "multiplier": return `${v}x`;
    case "number":     return Number(v).toLocaleString();
    default:           return String(v);
  }
}

function isMetaLoginUrl(url) {
  try {
    const u = new URL(url);
    return u.protocol === "https:"
      && (u.hostname === "www.facebook.com" || u.hostname === "facebook.com")
      && /\/dialog\/oauth$/.test(u.pathname);
  } catch { return false; }
}

function highlightForMetric(key, value) {
  if (value == null) return undefined;
  if (key === "roas")                return value >= 5 ? "good" : value >= 2 ? "ok" : "bad";
  if (key === "gp_roas")             return value >= 2 ? "good" : value >= 1 ? "ok" : "bad";
  if (key === "qualified_lead_rate") return value >= 50 ? "good" : value >= 25 ? "ok" : "bad";
  if (key === "out_of_area_rate")    return value <= 5 ? "good" : value <= 15 ? "ok" : "bad";
  return undefined;
}

// Admin-only Meta (Facebook/Instagram) connection card. Shows connection health
// and, when disconnected, a one-time paste form for the App Secret + access
// token. The token is exchanged server-side for a long-lived, auto-refreshing
// token stored in Supabase (meta_tokens, RLS-locked) — the browser never reads
// it back. Inputs are masked and only POSTed, never placed in a URL.
function MetaConnectionCard() {
  const [status, setStatus]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [appSecret, setAppSecret] = useState("");
  const [token, setToken]     = useState("");
  const [busy, setBusy]       = useState(false);
  const [oauthBusy, setOauthBusy] = useState(false);
  const [msg, setMsg]         = useState("");
  const [err, setErr]         = useState("");

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch("/api/meta-sync?action=token-status").then(r => r.json());
      setStatus(r);
      if (!r.connected) setShowForm(true);
    } catch { /* leave status null */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Facebook sends the browser back to /marketing?meta_oauth=… after reconnect.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get("meta_oauth");
    if (!flag) return;
    if (flag === "connected") {
      const n = params.get("meta_leads");
      const extra = params.get("meta_error");
      const count = n != null ? ` Backfilled ${n} lead${n === "1" ? "" : "s"} from the last 90 days.` : "";
      setMsg(`Meta reconnected for lead forms.${count}${extra ? ` ${extra}` : ""}`);
    } else {
      setErr(params.get("meta_error") || "Meta reconnect failed. Try Reconnect Meta again.");
    }
    params.delete("meta_oauth");
    params.delete("meta_leads");
    params.delete("meta_error");
    const qs = params.toString();
    window.history.replaceState(null, "", `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`);
  }, []);

  const connect = useCallback(async () => {
    if (!token.trim()) { setErr("Paste the access token."); return; }
    if (!appSecret.trim() && !status?.has_app_secret) { setErr("Paste the App Secret (needed on first connect)."); return; }
    setBusy(true); setMsg(""); setErr("");
    try {
      const payload = { token: token.trim() };
      if (appSecret.trim()) payload.app_secret = appSecret.trim();
      const r = await apiFetch("/api/meta-sync?action=save-token", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const body = await r.json();
      if (!r.ok || !body.ok) throw new Error(body.error || `HTTP ${r.status}`);
      const scopeNote = Array.isArray(body.scopes) && body.scopes.length ? ` Scopes: ${body.scopes.join(", ")}.` : "";
      setMsg(`Connected as ${body.fb_user || "Meta user"}. Token stored & auto-refreshing.${scopeNote}`);
      setToken(""); setAppSecret(""); setShowForm(false);
      await loadStatus();
    } catch (e) {
      setErr(e.message);
    } finally { setBusy(false); }
  }, [token, appSecret, status, loadStatus]);

  const reconnectLeadForms = useCallback(async () => {
    setOauthBusy(true); setMsg(""); setErr("");
    try {
      const r = await apiFetch("/api/meta-sync?action=oauth-start", {
        headers: { Accept: "application/json" },
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || !body.ok || !body.url) throw new Error(body.error || `HTTP ${r.status}`);
      if (!isMetaLoginUrl(body.url)) throw new Error("Meta login URL was not recognized.");
      window.location.assign(body.url);
    } catch (e) {
      setErr(e.message);
      setOauthBusy(false);
    }
  }, []);

  if (loading) return null;

  const connected = status?.connected && status?.source === "store";
  const needsLeadReconnect = status?.leads_retrieval !== true;
  const dLeft = status?.days_until_expiry;

  return (
    <div style={{
      border: `1px solid ${connected ? "#2e7d32" : "#a5701a"}`,
      background: connected ? "rgba(46,125,50,0.08)" : "rgba(165,112,26,0.10)",
      borderRadius: 8, padding: "12px 16px", marginBottom: 14,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontWeight: 700, color: connected ? "#69b36b" : "#d9a441" }}>
          {connected ? "✓ Meta Ads connected" : "○ Meta Ads not connected"}
        </span>
        {connected && (
          <span style={{ fontSize: 13, color: "#9aa0a6" }}>
            {status.fb_user ? `${status.fb_user} · ` : ""}
            {dLeft != null ? `token valid ${dLeft} more days · auto-refreshing` : "auto-refreshing"}
          </span>
        )}
        <div style={{ flex: 1 }} />
        {needsLeadReconnect && (
          <button style={S.btnPrimary} onClick={reconnectLeadForms} disabled={oauthBusy || busy}>
            {oauthBusy ? "Opening Facebook…" : "Reconnect Meta (for lead forms)"}
          </button>
        )}
        <button style={S.btn} onClick={() => setShowForm(v => !v)} disabled={oauthBusy}>
          {showForm ? "Cancel" : connected ? "Update token" : "Connect Meta"}
        </button>
      </div>

      {needsLeadReconnect && (
        <div style={{ marginTop: 8, fontSize: 12.5, color: "#9aa0a6", lineHeight: 1.5 }}>
          Opens Facebook Login so this account can grant Instant Form access
          {" "}(<code>leads_retrieval</code>, <code>pages_manage_metadata</code>).
          The token stays on the server.
        </div>
      )}
      {connected && status?.missing_scopes?.length > 0 && (
        <div style={{ marginTop: 8, fontSize: 13, color: "#d9a441" }}>
          Missing Meta permissions: {status.missing_scopes.join(", ")}.
        </div>
      )}
      {status?.lead_access_error && (
        <div style={{ marginTop: 8, fontSize: 13, color: "#e26d6d" }}>{status.lead_access_error}</div>
      )}
      {connected && status?.leads_retrieval && !status?.lead_access_error && (
        <div style={{ marginTop: 8, fontSize: 13, color: "#69b36b" }}>Lead forms connected.</div>
      )}

      {showForm && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8, maxWidth: 640 }}>
          <div style={{ fontSize: 12.5, color: "#9aa0a6", lineHeight: 1.5 }}>
            Paste your Meta <b>App Secret</b> (App Settings → Basic → App Secret) and a <b>user access token</b>
            {" "}(Graph API Explorer, app <b>ElecBid</b>, with <code>ads_read, ads_management, business_management</code>).
            Both are stored server-side only and never shown again. The token is auto-converted to a 60-day
            long-lived token and refreshed daily so it never expires.
          </div>
          <input
            type="password" placeholder={status?.has_app_secret ? "App Secret (optional — reusing stored)" : "App Secret"} value={appSecret}
            onChange={e => setAppSecret(e.target.value)} style={S.input} autoComplete="off"
          />
          <input
            type="password" placeholder="User access token" value={token}
            onChange={e => setToken(e.target.value)} style={S.input} autoComplete="off"
          />
          <div>
            <button style={S.btnPrimary} onClick={connect} disabled={busy}>
              {busy ? "Connecting…" : "Connect & store token"}
            </button>
          </div>
        </div>
      )}

      {msg && <div style={{ marginTop: 8, fontSize: 13, color: "#69b36b" }}>{msg}</div>}
      {err && <div style={{ marginTop: 8, fontSize: 13, color: "#e26d6d" }}>{err}</div>}
    </div>
  );
}

// Trends & ROAS tab: real gross-profit ROAS (Meta spend vs Jobber booked
// revenue, from the attribution bridge) plus per-campaign efficiency (CTR / CPC /
// cost-per-Meta-lead). Finance-gated. Reads the marketing-engine endpoints.
function TrendsTab() {
  const perms = usePermissions() || {};
  const canSeeFinance = !!(perms.isAdmin || perms.isOwner || perms.hasPermission?.("view_marketing_finance"));
  const [days, setDays]     = useState("all");
  const [trends, setTrends] = useState(null);
  const [attr, setAttr]     = useState(null);
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState("");
  const [openAgency, setOpenAgency] = useState(null); // drill-down: which agency's leads to show

  const load = useCallback(async () => {
    if (!canSeeFinance) return;
    setBusy(true); setErr("");
    try {
      const [t, a] = await Promise.all([
        apiFetch(`/api/marketing-engine?action=trends&days=${days}`).then(r => r.json()),
        apiFetch(`/api/marketing-engine?action=attribution&days=${days}`).then(r => r.json()),
      ]);
      if (!t.ok) throw new Error(t.error || "trends failed");
      if (!a.ok) throw new Error(a.error || "attribution failed");
      setTrends(t); setAttr(a);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }, [days, canSeeFinance]);
  useEffect(() => { load(); }, [load]);

  if (!canSeeFinance) return <div style={{ padding: 20, color: "#9aa0a6" }}>Trends require marketing-finance access.</div>;

  const num = (n) => Number(n || 0).toLocaleString();
  const ts = {
    card: { flex: "1 1 130px", minWidth: 130, background: "#1c1f24", border: "1px solid #2a2e35", borderRadius: 8, padding: "12px 14px" },
    cardVal: { fontSize: 22, fontWeight: 700, color: "#e8eaed" },
    cardLbl: { fontSize: 12, color: "#9aa0a6", marginTop: 2 },
    table: { width: "100%", borderCollapse: "collapse", fontSize: 13, marginTop: 8 },
    th: { textAlign: "left", padding: "8px 10px", borderBottom: "2px solid #2a2e35", color: "#9aa0a6", fontWeight: 600 },
    thR: { textAlign: "right", padding: "8px 10px", borderBottom: "2px solid #2a2e35", color: "#9aa0a6", fontWeight: 600 },
    td: { padding: "8px 10px", borderBottom: "1px solid #23262c", color: "#d5d8dc" },
    tdR: { padding: "8px 10px", borderBottom: "1px solid #23262c", color: "#d5d8dc", textAlign: "right" },
    h3: { margin: "6px 0", color: "#e8eaed", fontSize: 16 },
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={S.filterBar}>
        <FilterField label="Window">
          <select value={days} onChange={e => setDays(e.target.value)} style={S.input}>
            <option value="all">All available history</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={180}>Last 180 days</option>
          </select>
        </FilterField>
        <button onClick={load} disabled={busy} style={S.btnPrimary}>{busy ? "Loading…" : "Refresh"}</button>
      </div>
      {err && <div style={S.errorBanner}>{err}<button style={S.bannerClose} onClick={() => setErr("")}>×</button></div>}

      {attr && (
        <div>
          <div style={ts.h3}>Request-based attribution — Meta spend vs linked Jobber jobs</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            <div style={ts.card}><div style={ts.cardVal}>{fmtMoney(attr.spend)}</div><div style={ts.cardLbl}>Ad spend</div></div>
            <div style={ts.card}><div style={ts.cardVal}>{num(attr.leads)}</div><div style={ts.cardLbl}>Paid-social leads</div></div>
            <div style={ts.card}><div style={ts.cardVal}>{num(attr.won)}</div><div style={ts.cardLbl}>Jobs won</div></div>
            <div style={ts.card}><div style={ts.cardVal}>{fmtMoney(attr.revenue)}</div><div style={ts.cardLbl}>Booked revenue</div></div>
            <div style={ts.card}><div style={ts.cardVal}>{fmtMoney(attr.gp)}</div><div style={ts.cardLbl}>Gross profit</div></div>
            <div style={ts.card}><div style={{ ...ts.cardVal, color: attr.gp_roas >= 2 ? "#69b36b" : attr.gp_roas >= 1 ? "#d9a441" : "#e26d6d" }}>{attr.gp_roas != null ? `${attr.gp_roas}x` : "—"}</div><div style={ts.cardLbl}>Gross-profit ROAS</div></div>
            <div style={ts.card}><div style={ts.cardVal}>{attr.cost_per_won != null ? fmtMoney(attr.cost_per_won) : "—"}</div><div style={ts.cardLbl}>Cost / booked job</div></div>
          </div>
          <div style={{ fontSize: 12, color: "#9aa0a6", marginTop: 8 }}>{attr.note} This is a request-linked booked-job snapshot, not collected cash or verified invoice revenue.</div>

          {attr.by_agency?.length > 0 && (
            <>
              <div style={{ ...ts.h3, marginTop: 16 }}>By agency — request-linked booked revenue and profit</div>
              <table style={ts.table}>
                <thead><tr>
                  <th style={ts.th}>Agency</th><th style={ts.thR}>Spend</th><th style={ts.thR}>Leads</th>
                  <th style={ts.thR}>Won</th><th style={ts.thR}>Revenue</th><th style={ts.thR}>Gross profit</th>
                  <th style={ts.thR}>GP ROAS</th><th style={ts.thR}>$/lead</th>
                </tr></thead>
                <tbody>
                  {attr.by_agency.map(a => (
                    <tr
                      key={a.agency}
                      onClick={() => setOpenAgency(openAgency === a.agency ? null : a.agency)}
                      style={{ cursor: "pointer", background: openAgency === a.agency ? "#22262d" : "transparent" }}
                      title="Click to see the leads & jobs behind this row"
                    >
                      <td style={ts.td}>{openAgency === a.agency ? "▾ " : "▸ "}{a.agency}</td>
                      <td style={ts.tdR}>{fmtMoney(a.spend)}</td>
                      <td style={ts.tdR}>{num(a.leads)}</td>
                      <td style={ts.tdR}>{num(a.won)}</td>
                      <td style={ts.tdR}>{fmtMoney(a.revenue)}</td>
                      <td style={ts.tdR}>{fmtMoney(a.gp)}</td>
                      <td style={{ ...ts.tdR, color: a.gp_roas >= 2 ? "#69b36b" : a.gp_roas >= 1 ? "#d9a441" : (a.gp_roas != null ? "#e26d6d" : "#9aa0a6") }}>{a.gp_roas != null ? `${a.gp_roas}x` : "—"}</td>
                      <td style={ts.tdR}>{a.cost_per_lead != null ? fmtMoney(a.cost_per_lead) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: 12, color: "#9aa0a6", marginTop: 4 }}>Click an agency to see every lead &amp; job behind the number.</div>

              {/* Drill-down: the actual leads/jobs behind the selected agency */}
              {openAgency && attr.records?.length > 0 && (
                <table style={ts.table}>
                  <thead><tr>
                    <th style={ts.th}>Date</th><th style={ts.th}>Client</th><th style={ts.th}>Request</th>
                    <th style={ts.th}>Stage</th><th style={ts.th}>Quote #</th><th style={ts.th}>Job #</th>
                    <th style={ts.thR}>Revenue</th><th style={ts.thR}>Gross profit</th>
                  </tr></thead>
                  <tbody>
                    {attr.records.filter(r => r.agency === openAgency).map((r, i) => (
                      <tr key={i} style={{ background: r.stage === "won" ? "rgba(46,125,50,0.10)" : "transparent" }}>
                        <td style={ts.td}>{r.requested_date ? String(r.requested_date).slice(0, 10) : "—"}</td>
                        <td style={ts.td}>{r.client || "—"}</td>
                        <td style={{ ...ts.td, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.title || ""}>{r.title || "—"}</td>
                        <td style={ts.td}>{r.stage}</td>
                        <td style={ts.td}>{r.quote_num || "—"}</td>
                        <td style={ts.td}>{r.job_num || "—"}</td>
                        <td style={ts.tdR}>{r.revenue ? fmtMoney(r.revenue) : "—"}</td>
                        <td style={ts.tdR}>{r.gp ? fmtMoney(r.gp) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}

          {attr.by_service_category?.length > 0 && (
            <table style={ts.table}>
              <thead><tr>
                <th style={ts.th}>Service line</th><th style={ts.thR}>Leads</th><th style={ts.thR}>Quoted</th>
                <th style={ts.thR}>Won</th><th style={ts.thR}>Revenue</th><th style={ts.thR}>Gross profit</th>
              </tr></thead>
              <tbody>
                {attr.by_service_category.map(c => (
                  <tr key={c.category}>
                    <td style={ts.td}>{c.category}</td>
                    <td style={ts.tdR}>{num(c.leads)}</td>
                    <td style={ts.tdR}>{num(c.quoted)}</td>
                    <td style={ts.tdR}>{num(c.won)}</td>
                    <td style={ts.tdR}>{fmtMoney(c.revenue)}</td>
                    <td style={ts.tdR}>{fmtMoney(c.gp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {trends?.campaigns?.length > 0 && (
        <div>
          <div style={ts.h3}>Campaign efficiency (including historical campaigns, by spend)</div>
          <div style={{ fontSize: 12, color: "#9aa0a6" }}>Mapped lead fields can omit website custom events or overlap aliases. Use Swift takeover for the reconciled event definitions and coverage; these fields are not unique customers.</div>
          <table style={ts.table}>
            <thead><tr>
              <th style={ts.th}>Campaign</th><th style={ts.th}>Manager</th>
              <th style={ts.thR}>Spend</th><th style={ts.thR}>CTR</th><th style={ts.thR}>CPC</th>
              <th style={ts.thR}>Mapped lead events</th><th style={ts.thR}>$/mapped event</th>
            </tr></thead>
            <tbody>
              {trends.campaigns.map((c, i) => (
                <tr key={i}>
                  <td style={ts.td}>{c.name}</td>
                  <td style={ts.td}>{c.source}</td>
                  <td style={ts.tdR}>{fmtMoney(c.spend)}</td>
                  <td style={ts.tdR}>{c.ctr != null ? `${c.ctr}%` : "—"}</td>
                  <td style={ts.tdR}>{c.cpc != null ? fmtMoney(c.cpc) : "—"}</td>
                  <td style={ts.tdR}>{num(c.meta_leads)}</td>
                  <td style={ts.tdR}>{c.cost_per_meta_lead != null ? fmtMoney(c.cost_per_meta_lead) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Full source history, agency report evidence, and an independent page-only launch switch.
function SwiftTakeoverTab() {
  const perms = usePermissions() || {};
  const canFinance = !!(perms.isAdmin || perms.isOwner || perms.hasPermission?.("view_marketing_finance"));
  const isAdmin = !!(perms.isAdmin || perms.isOwner);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(null);
  const [error, setError] = useState("");
  const [pageError, setPageError] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState("");
  const [verificationRef, setVerificationRef] = useState("");
  const [backfillNote, setBackfillNote] = useState("");

  const readJson = useCallback(async (url, options) => {
    const response = await apiFetch(url, options);
    const result = await response.json();
    if (!response.ok || result.ok === false) throw new Error(result.error || `Request failed (${response.status}).`);
    return result;
  }, []);
  const load = useCallback(async () => {
    if (!canFinance) return;
    setBusy(true); setError(""); setPageError("");
    const results = await Promise.allSettled([
      readJson("/api/marketing-engine?action=lifetime"),
      isAdmin ? readJson("/api/generator-campaign?action=status") : Promise.resolve(null),
    ]);
    if (results[0].status === "fulfilled") setData(results[0].value);
    else setError(results[0].reason.message);
    if (results[1].status === "fulfilled") setPage(results[1].value);
    else setPageError(results[1].reason.message);
    setBusy(false);
  }, [canFinance, isAdmin, readJson]);
  useEffect(() => { load(); }, [load]);

  const backfillHistory = async () => {
    setActionBusy("backfill"); setError(""); setBackfillNote("");
    try {
      const start = data?.coverage?.first_campaign_date;
      const account = data?.sources?.[0]?.external_account_id;
      const startMs = start ? Date.parse(`${start}T00:00:00Z`) : NaN;
      if (!account || !Number.isFinite(startMs) || startMs > Date.now()) throw new Error("A valid campaign start and Meta account are required.");
      const days = Math.min(36500, Math.max(1, Math.ceil((Date.now() - startMs) / 86400000) + 1));
      const params = new URLSearchParams({ action: "sync", account, days: String(days) });
      const result = await readJson(`/api/meta-sync?${params}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "sync", account, days }),
      });
      if (!Array.isArray(result.results) || !result.results.length) throw new Error("Meta did not confirm a history sync result.");
      const failures = result.results.filter(item => item.ok !== true);
      if (failures.length) throw new Error(`History sync partially failed: ${failures.map(item => item.error || item.name || item.source || "unconfirmed result").join(" · ")}`);
      await load();
      setBackfillNote(`Meta history sync completed from the first campaign (${start}). Dates with no delivery can still have no metric rows. Live ads and budgets were not changed.`);
    } catch (e) { setError(e.message); }
    finally { setActionBusy(""); }
  };
  const pageAction = async (action, body) => {
    setActionBusy(action); setPageError("");
    try {
      await readJson(`/api/generator-campaign?action=${action}`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      setPage(await readJson("/api/generator-campaign?action=status"));
      if (action === "verify-intake") setVerificationRef("");
    } catch (e) { setPageError(e.message); }
    finally { setActionBusy(""); }
  };
  const preview = async () => {
    const popup = window.open("about:blank", "_blank");
    if (popup) popup.opener = null;
    setActionBusy("preview"); setPageError("");
    try {
      const response = await apiFetch(page?.preview_path || "/api/generator-campaign?action=preview");
      if (!response.ok) throw new Error("Could not open the authenticated generator preview.");
      const html = (await response.text()).replace(/\b(src|href)=(["'])\/(?!\/)/g, `$1=$2${window.location.origin}/`);
      const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
      if (popup) popup.location.href = url;
      else { URL.revokeObjectURL(url); throw new Error("Allow popups for ElecBid to open the preview."); }
      setTimeout(() => URL.revokeObjectURL(url), 300000);
    } catch (e) { if (popup) popup.close(); setPageError(e.message); }
    finally { setActionBusy(""); }
  };
  const download = (name, content, type) => {
    const url = URL.createObjectURL(new Blob([content], { type }));
    const link = document.createElement("a");
    link.href = url; link.download = name; link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const downloadCsv = () => {
    const keys = ["month", "spend", "impressions", "all_clicks", "link_clicks", "landing_page_views", "custom_events", "generic_custom_events", "call_events"];
    const lines = [keys.join(","), ...(data?.monthly || []).map(row => keys.map(key => row[key] == null ? "" : String(row[key])).join(","))];
    download("swift-history-monthly.csv", lines.join("\r\n"), "text/csv;charset=utf-8");
  };
  if (!canFinance) return <div style={S.card}>Swift history requires marketing-finance access.</div>;
  const number = value => value == null ? "—" : Number(value).toLocaleString("en-CA");
  const money = value => value == null ? "—" : data?.currency ? Number(value).toLocaleString("en-CA", { style: "currency", currency: data.currency }) : Number(value).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const coverage = data?.coverage || {};
  const enabled = !!(page?.page_enabled ?? page?.enabled);
  const serving = enabled && !!page?.readiness?.ready;
  const blocked = page?.readiness?.blocking_reasons || [];
  const th = { textAlign: "left", padding: "10px 8px", borderBottom: "1px solid #3c424a", color: "#aeb6c1", fontSize: 12, whiteSpace: "nowrap" };
  const td = { padding: "10px 8px", borderBottom: "1px solid #2a3038", color: "#e3e8ee", fontSize: 13 };
  const table = { width: "100%", borderCollapse: "collapse" };
  const small = { fontSize: 12, color: "#aeb6c1", lineHeight: 1.6 };
  const kpis = [["Recorded media spend", money(data?.totals?.spend)], ["Impressions", number(data?.totals?.impressions)], ["Link clicks", number(data?.totals?.link_clicks)], ["Landing-page views", number(data?.totals?.landing_page_views)], ["Custom events · unverified", number(data?.totals?.custom_events)]];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div style={S.filterBar}>
        <div style={{ flex: 1 }}><div style={S.cardTitle}>SwiftSocial · full available history</div><div style={small}>Live read of every saved source metric. Agency reports stay separate from conversion events.</div></div>
        <button onClick={load} disabled={busy} style={S.btnPrimary}>{busy ? "Loading…" : "Refresh history"}</button>
        {isAdmin && <button onClick={backfillHistory} disabled={busy || !!actionBusy || !data?.coverage?.first_campaign_date || !data?.sources?.[0]?.external_account_id} style={S.btn}>{actionBusy === "backfill" ? "Backfilling Meta…" : "Backfill from first campaign"}</button>}
        <button onClick={() => download("swift-history-audit.json", JSON.stringify(data, null, 2), "application/json")} disabled={!data || busy} style={S.btn}>Export JSON</button>
        <button onClick={downloadCsv} disabled={!data || busy} style={S.btn}>Monthly CSV</button>
      </div>
      {error && <div role="alert" style={S.errorBanner}>{error}</div>}
      {backfillNote && <div role="status" style={S.statusBanner}>{backfillNote}</div>}
      {data && <>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
          {kpis.map(([label, value]) => <div key={label} style={S.card}><div style={{ fontSize: 24, fontWeight: 700, color: "#e8eaed" }}>{value}</div><div style={small}>{label}</div></div>)}
        </div>
        <div style={S.card}>
          <div style={S.cardTitle}>Coverage and measurement</div>
          <p style={small}>Onboarding evidence: {coverage.onboarding_date || "not recorded"} · first campaign: {coverage.first_campaign_date || "not recorded"}. Saved delivery: {coverage.first_saved_date || "none"} through {coverage.last_saved_date || "none"}. Last sync: {coverage.last_sync ? new Date(coverage.last_sync).toLocaleString() : "unknown"}.</p>
          <p style={small}>{number(coverage.ads_with_metrics)} ads with metrics across {number(coverage.campaigns)} campaigns. Dates before the first saved metric still need a Meta backfill check. {data.currency ? `Account currency: ${data.currency}.` : "Account currency unverified; monetary values are account units."} Agency fees and tax are excluded.</p>
          <p style={small}>Days without saved rows: {coverage.days_without_rows?.length ? coverage.days_without_rows.join(", ") : "none inside the saved range"}. These may be pauses in delivery. Boundary months can be partial.</p>
          <p style={{ ...small, color: "#f0c56c" }}>Custom conversion {data.custom_event?.id} has an unverified definition. Events can overlap and are not unique leads or booked jobs. Campaign-level matched leads: {number(data.campaign_lead_coverage?.records)}; linked jobs: {number(data.campaign_lead_coverage?.linked_jobs)}. Agency-level request attribution remains available in Trends &amp; ROAS.</p>
        </div>
        {data.finance && <SwiftFinanceCard finance={data.finance} isAdmin={isAdmin} readJson={readJson} />}
        <div style={S.card}>
          <div style={S.cardTitle}>Calendar months</div>
          <div style={{ overflowX: "auto" }}><table style={table}><thead><tr>{["Month", "Media spend", "Impressions", "Link clicks", "Page views", "Custom events · unverified"].map(label => <th key={label} style={th}>{label}</th>)}</tr></thead><tbody>
            {data.monthly?.map(row => <tr key={row.month}><td style={td}>{row.month}</td><td style={td}>{money(row.spend)}</td><td style={td}>{number(row.impressions)}</td><td style={td}>{number(row.link_clicks)}</td><td style={td}>{number(row.landing_page_views)}</td><td style={td}>{number(row.custom_events)}</td></tr>)}
          </tbody></table></div>
        </div>
        <div style={S.card}>
          <div style={S.cardTitle}>Historical ads · ranked by unverified event volume</div>
          <div style={small}>Use these as test hypotheses. This ranking does not establish profit, unique leads, or statistical superiority.</div>
          <div style={{ overflowX: "auto" }}><table style={table}><thead><tr>{["Ad", "Status", "Spend", "Custom events", "Cost / event"].map(label => <th key={label} style={th}>{label}</th>)}</tr></thead><tbody>
            {data.top_ads?.map(row => <tr key={row.id}><td style={{ ...td, minWidth: 260 }}>{row.name}{row.headline && <div style={small}>{row.headline}</div>}</td><td style={td}>{row.status || "—"}</td><td style={td}>{money(row.spend)}</td><td style={td}>{number(row.custom_events)}</td><td style={td}>{money(row.cost_per_unverified_event)}</td></tr>)}
          </tbody></table></div>
        </div>
        <div style={S.card}>
          <div style={S.cardTitle}>Swift reports compared with saved metrics</div>
          <div style={small}>Reported leads and CPL come from dated agency emails. Report windows overlap; do not sum them into lifetime leads.</div>
          <div style={{ overflowX: "auto" }}><table style={table}><thead><tr>{["Report window", "Reported leads", "Reported CPL", "Synced spend", "Synced custom events"].map(label => <th key={label} style={th}>{label}</th>)}</tr></thead><tbody>
            {data.report_windows?.map(row => <tr key={row.period_start}><td style={td}>{row.period_start} – {row.period_end}</td><td style={td}>{number(row.reported_leads)}</td><td style={td}>{money(row.reported_cpl)}</td><td style={td}>{money(row.synced?.spend)}</td><td style={td}>{number(row.synced?.custom_events)}</td></tr>)}
          </tbody></table></div>
          <details style={{ ...small, marginTop: 14 }}><summary>Audit notes</summary><ul>{data.notes?.map(note => <li key={note}>{note}</li>)}</ul></details>
        </div>
      </>}
      {isAdmin && <div style={S.card}>
        <div style={S.cardTitle}>Generator landing page · staged launch</div>
        <p style={small}>The page has its own launch switch. It opens or closes the in-house landing page; Meta campaigns, ad status and budgets are controlled separately.</p>
        {pageError && <div role="alert" style={S.errorBanner}>{pageError}</div>}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ color: enabled ? "#a5ce3a" : "#f0c56c", fontWeight: 700 }}>{page ? serving ? "Page serving" : enabled ? "Page enabled · readiness pending" : "Page off · preparation" : "Checking page status…"}</span>
          <button onClick={preview} disabled={!page || !!actionBusy} style={S.btn}>Preview original landing page</button>
          <button role="switch" aria-checked={enabled} onClick={() => pageAction("set-enabled", { enabled: !enabled })} disabled={!page || !!actionBusy || (!enabled && !page.can_enable)} style={S.btnPrimary}>{actionBusy === "set-enabled" ? "Saving…" : enabled ? "Turn page off" : "Enable landing page"}</button>
          {serving && page.public_path && <a href={page.public_path} target="_blank" rel="noopener noreferrer" style={{ color: "#a5ce3a" }}>Open landing page ↗</a>}
        </div>
        {page?.readiness?.checks?.length > 0 && <ul style={small}>{page.readiness.checks.map(check => <li key={check.key} style={{ color: check.ok ? "#b7d8b2" : "#f0c56c" }}>{check.ok ? "Ready: " : "Pending: "}{check.label}{check.detail ? ` — ${check.detail}` : ""}</li>)}</ul>}
        {blocked.length > 0 && <p style={{ ...small, color: "#f0c56c" }}>Before enabling: {blocked.map(reason => typeof reason === "string" ? reason : reason.detail || reason.label).join(" · ")}</p>}
        {page?.test_form_path && <div style={{ ...small, marginTop: 16 }}>
          <a href={page.test_form_path} target="_blank" rel="noopener noreferrer" style={{ color: "#a5ce3a" }}>Open dedicated test intake ↗</a>
          <div>Submitting the test form creates a real tagged test request. Once its Jobber handoff is verified, enter its request reference below.</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}><input aria-label="Verified test request reference" value={verificationRef} onChange={e => setVerificationRef(e.target.value)} placeholder="Request reference, e.g. R-123" style={S.input} /><button disabled={!verificationRef.trim() || !!actionBusy} style={S.btn} onClick={() => pageAction("verify-intake", { reference: verificationRef.trim() })}>{actionBusy === "verify-intake" ? "Checking…" : "Verify intake handoff"}</button></div>
        </div>}
        {page?.verification && <p style={small}>Verified request: {page.verification.reference} · {page.verification.verified_at ? new Date(page.verification.verified_at).toLocaleString() : ""}</p>}
      </div>}
    </div>
  );
}

// Finance evidence arrives only through the authenticated lifetime response.
// Never import the server snapshot into this browser bundle.
function SwiftFinanceCard({ finance, isAdmin, readJson }) {
  const [bills, setBills] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const refreshBills = async () => {
    setBusy(true); setError("");
    try {
      const result = await readJson("/api/quickbooks-bill", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "vendor-bills", name: "Swift" }),
      });
      setBills({ ...result, refreshed_at: new Date().toISOString() });
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const small = { color: "#aeb6c1", fontSize: 12, lineHeight: 1.6 };
  const td = { padding: "9px 8px", borderBottom: "1px solid #2a3038", color: "#e3e8ee", fontSize: 13, verticalAlign: "top" };
  const th = { ...td, color: "#aeb6c1", textAlign: "left", fontSize: 12 };
  const table = { width: "100%", borderCollapse: "collapse" };
  const amount = (value, currency = "CAD") => value == null ? "—" : `${Number(value).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
  const summary = finance.summary || {};
  const fees = finance.fees || {};
  const cards = [
    ["Confirmed receipt faces · minimum", amount(summary.confirmed_receipt_face_total, "USD")],
    ["Unique invoice revenue · before tax", amount(summary.invoiced_net)],
    ["Paid-status invoices · including tax", amount(summary.paid_status_invoice_gross)],
    ["Open invoice balance", amount(summary.open_invoice_balance)],
    ["Estimated GP · before ads / fees / overhead", amount(summary.estimated_gross_profit_before_advertising_and_overhead)],
  ];
  return <div style={S.card}>
    <div style={{ display: "flex", gap: 12, justifyContent: "space-between", flexWrap: "wrap", alignItems: "center" }}>
      <div><div style={S.cardTitle}>Agency fees and converted work</div><div style={small}>Evidence snapshot · {finance.audit_date}. Live ad metrics above refresh separately.</div></div>
      {isAdmin && <button style={S.btn} disabled={busy} onClick={refreshBills}>{busy ? "Reading QuickBooks…" : "Refresh Swift bills from QuickBooks"}</button>}
    </div>
    <p style={small}>Confirmed {summary.confirmed_receipt_count || 0} distinct receipts. The signed contract specifies {amount(fees.service_fee, fees.currency)} plus {amount(fees.processing_fee, fees.currency)} processing per {fees.billing_cycle_days}-day cycle, with ad spend additional. Receipts omit a printed currency code; USD is the contract basis. Current renewal amount, later invoices and actual CAD card settlements still need reconciliation.</p>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(175px, 1fr))", gap: 10 }}>
      {cards.map(([label, value]) => <div key={label} style={{ padding: 12, background: "#171c22", borderRadius: 8 }}><div style={{ fontSize: 19, fontWeight: 700, color: "#e8eaed" }}>{value}</div><div style={small}>{label}</div></div>)}
    </div>
    <p style={{ ...small, color: "#f0c56c" }}>This is an incomplete payment ledger. Paid invoice status is separate from bank-settled cash. USD agency charges and CAD media spend cannot be combined without verified settlement amounts; no complete lifetime ROI is claimed.</p>
    <details style={small}><summary>Confirmed agency receipt evidence</summary><div style={{ overflowX: "auto" }}><table style={table}><thead><tr>{["Date", "Invoice", "Receipt face", "Evidence"].map(label => <th key={label} style={th}>{label}</th>)}</tr></thead><tbody>{finance.receipts?.map(receipt => <tr key={receipt.invoice}><td style={td}>{receipt.date}</td><td style={td}>{receipt.invoice}</td><td style={td}>{amount(receipt.amount, "USD contract basis")}</td><td style={td}><a href={receipt.url} target="_blank" rel="noopener noreferrer" style={{ color: "#a5ce3a" }}>Receipt ↗</a></td></tr>)}</tbody></table></div></details>
    <div style={{ ...S.cardTitle, marginTop: 18 }}>Explicitly linked converted jobs</div>
    <p style={small}>{summary.linked_converted_jobs || 0} linked jobs: {summary.completed_generator_installations || 0} completed generator installations, {summary.completed_ancillary_jobs || 0} ancillary job and {summary.booked_uninvoiced_generator_jobs || 0} upcoming uninvoiced job. Requests and invoices were deduplicated for this snapshot. Attribution follows request/quote-to-job links, not a claim that a specific ad caused the sale.</p>
    <div style={{ overflowX: "auto" }}><table style={table}><thead><tr>{["Job", "Work / state", "Unique invoices", "Net invoiced · CAD", "Open balance · CAD", "Estimated GP · CAD"].map(label => <th key={label} style={th}>{label}</th>)}</tr></thead><tbody>
      {finance.linked_jobs?.map(job => <tr key={job.job_number}>
        <td style={{ ...td, minWidth: 130 }}><a href={job.job_url} target="_blank" rel="noopener noreferrer" style={{ color: "#a5ce3a" }}>#{job.job_number} · {job.client} ↗</a></td>
        <td style={{ ...td, minWidth: 180 }}>{job.classification}{job.booked_uninvoiced_net > 0 && <div style={small}>Booked {amount(job.booked_uninvoiced_net)}; excluded from invoiced revenue and realized profit.</div>}</td>
        <td style={td}>{job.invoices?.length ? job.invoices.map(invoice => <div key={invoice.invoice_number}>#{invoice.invoice_number} · {String(invoice.status || "unknown").replace(/_/g, " ")}</div>) : "None yet"}</td>
        <td style={td}>{amount(job.invoiced_net)}</td><td style={td}>{amount(job.open_invoice_balance)}</td><td style={td}>{amount(job.estimated_gross_profit_from_invoice_net)}</td>
      </tr>)}
    </tbody></table></div>
    <p style={small}>GP uses pre-tax unique invoice revenue less current synced job costs; future labour, materials, credits and cost corrections can change it. Advertising, agency fees and overhead are excluded. Archived quotes and upcoming projected profit are not cash or completed-job profit.</p>
    <details style={small}><summary>Invoice and cost reconciliation notes</summary><ul>{finance.linked_jobs?.flatMap(job => (job.snapshot_limits || []).map(note => <li key={`${job.job_number}-${note}`}>#{job.job_number}: {note}</li>))}</ul></details>
    <div style={{ ...S.cardTitle, marginTop: 18 }}>Fee-reallocation planning · no budget change</div>
    <p style={small}>The following 30-day incremental tests assume the same raw-lead CPL of {amount(finance.reallocation?.cpl_basis?.cad_low)}–{amount(finance.reallocation?.cpl_basis?.cad_high)} reported in the recent agency windows. This is arithmetic, not a lead or installation forecast. Actual marginal performance and sales capacity may differ.</p>
    <div style={{ overflowX: "auto" }}><table style={table}><thead><tr>{["Extra media / day", "30-day spend", "Raw-lead arithmetic if CPL holds", "Install forecast"].map(label => <th key={label} style={th}>{label}</th>)}</tr></thead><tbody>{finance.reallocation?.scenarios?.map(scenario => <tr key={scenario.incremental_cad_per_day}><td style={td}>+{amount(scenario.incremental_cad_per_day)}</td><td style={td}>{amount(scenario.incremental_spend_cad)}</td><td style={td}>{scenario.arithmetic_additional_reported_leads_if_same_cpl_low}–{scenario.arithmetic_additional_reported_leads_if_same_cpl_high}</td><td style={td}>Not estimated</td></tr>)}</tbody></table></div>
    <p style={small}>Start with the smallest controlled test after tracking and intake verification. Reinvest only fees that actually cease, converted at actual CAD settlement, after in-house tools and staff time.</p>
    {error && <div role="alert" style={S.errorBanner}>{error}</div>}
    {bills && <div style={{ marginTop: 18 }}><div style={S.cardTitle}>Supplemental QuickBooks vendor read</div><p style={small}>Read at {new Date(bills.refreshed_at).toLocaleString()}. Bill/credit balances and paid status are accounting records, not a bank-payment ledger. Do not total mixed currencies. Results can be bounded by the vendor query.</p>
      {bills.vendors?.length ? <div style={{ overflowX: "auto" }}><table style={table}><thead><tr>{["Vendor", "Date", "Bill / credit", "Face amount", "Currency", "Balance", "Bill status"].map(label => <th key={label} style={th}>{label}</th>)}</tr></thead><tbody>{bills.vendors.flatMap(vendor => (vendor.bills || []).map(bill => {
        const hasBalance = bill.balance != null && bill.balance !== "" && Number.isFinite(Number(bill.balance));
        const currency = typeof bill.currency === "string" ? bill.currency : bill.currency?.value || bill.currency_code || "unverified";
        return <tr key={`${vendor.id}-${bill.entity}-${bill.id}`}><td style={td}>{vendor.name}</td><td style={td}>{bill.date}</td><td style={td}>{bill.entity} #{bill.doc || bill.id}</td><td style={td}>{amount(bill.total, "")}</td><td style={td}>{currency}</td><td style={td}>{hasBalance ? amount(bill.balance, "") : "—"}</td><td style={td}>{bill.entity !== "Bill" ? "Credit" : !hasBalance ? "Unknown balance" : Number(bill.balance) === 0 ? "Paid status" : "Open balance"}</td></tr>;
      }))}</tbody></table></div> : <div style={small}>No matching Swift vendor bills were returned. This does not establish that no agency payments occurred.</div>}
    </div>}
  </div>;
}

// Control: pause/activate campaigns + set daily budget directly from ElecBid.
// Every change hits the live Meta campaign, is capped ($/day), logged, and
// reversible. Fires only on an explicit click (with confirm).
function ControlTab() {
  const perms = usePermissions() || {};
  const canSeeFinance = !!(perms.isAdmin || perms.isOwner || perms.hasPermission?.("view_marketing_finance"));
  const [data, setData]       = useState(null);
  const [actions, setActions] = useState([]);
  const [flags, setFlags]     = useState([]);
  const [busy, setBusy]       = useState(false);
  const [err, setErr]         = useState("");
  const [msg, setMsg]         = useState("");
  const [budgets, setBudgets] = useState({});
  const [applying, setApplying] = useState(null);
  const MS = "/api/meta-sync";
  const ts = {
    card: { background: "#1c1f24", border: "1px solid #2a2e35", borderRadius: 8, padding: 14, marginBottom: 10 },
    small: { fontSize: 12, color: "#9aa0a6" },
  };

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const [c, a, g] = await Promise.all([
        apiFetch(`${MS}?action=control-list`).then(r => r.json()),
        apiFetch(`${MS}?action=list-actions`).then(r => r.json()),
        apiFetch(`${MS}?action=guardian`).then(r => r.json()).catch(() => ({})),
      ]);
      if (!c.ok) throw new Error(c.error || "control-list failed");
      setData(c); setActions(a.actions || []); setFlags(g.findings || []);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = useCallback(async (c, status) => {
    if (!window.confirm(`${status === "PAUSED" ? "Pause" : "Activate"} "${c.name}" in Meta now? This changes the live campaign.`)) return;
    setApplying(c.external_id); setMsg(""); setErr("");
    try {
      const r = await apiFetch(`${MS}?action=set-campaign-status`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ external_id: c.external_id, status, requested_by: "dylan" }),
      });
      const b = await r.json();
      if (!r.ok || !b.ok) throw new Error(b.error || `HTTP ${r.status}`);
      setMsg(`"${c.name}" → ${status}.`);
      await load();
    } catch (e) { setErr(e.message); } finally { setApplying(null); }
  }, [load]);

  const applyBudget = useCallback(async (c) => {
    const v = Number(budgets[c.external_id]);
    if (!isFinite(v) || v <= 0) { setErr("Enter a valid daily budget ($/day)."); return; }
    if (!window.confirm(`Set "${c.name}" daily budget to $${v}/day in Meta? (hard cap $${data?.cap}/day)`)) return;
    setApplying(c.external_id); setMsg(""); setErr("");
    try {
      const r = await apiFetch(`${MS}?action=set-budget`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ external_id: c.external_id, daily_budget: v, requested_by: "dylan" }),
      });
      const b = await r.json();
      if (!r.ok || !b.ok) throw new Error(b.error || `HTTP ${r.status}`);
      setMsg(`"${c.name}" budget → $${b.daily_budget}/day${b.clamped ? ` (clamped to $${b.cap} cap)` : ""}.`);
      await load();
    } catch (e) { setErr(e.message); } finally { setApplying(null); }
  }, [budgets, data, load]);

  const toggleAutopilot = useCallback(async (c) => {
    const next = !c.autopilot;
    if (next && !window.confirm(`Enable AUTOPILOT for "${c.name}"?\n\nThe guardian may auto-pause it — but ONLY for genuine waste (junk boosts, or real spend with zero leads on a Meta-lead-form campaign). It never touches SwiftSocial form campaigns, never spends over the $/day cap, and every action is logged & reversible.`)) return;
    setApplying(c.external_id); setMsg(""); setErr("");
    try {
      const r = await apiFetch(`${MS}?action=set-autopilot`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ external_id: c.external_id, name: c.name, account: c.account, enabled: next }),
      });
      const b = await r.json();
      if (!r.ok || !b.ok) throw new Error(b.error || `HTTP ${r.status}`);
      setMsg(`Autopilot ${next ? "ON" : "off"} for "${c.name}".`);
      await load();
    } catch (e) { setErr(e.message); } finally { setApplying(null); }
  }, [load]);

  const revert = useCallback(async (id) => {
    setMsg(""); setErr("");
    try {
      const r = await apiFetch(`${MS}?action=revert-action`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id }),
      });
      const b = await r.json();
      if (!r.ok || !b.ok) throw new Error(b.error || `HTTP ${r.status}`);
      setMsg("Reverted."); await load();
    } catch (e) { setErr(e.message); }
  }, [load]);

  if (!canSeeFinance) return <div style={{ padding: 20, color: "#9aa0a6" }}>Campaign control requires marketing-finance access.</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={S.filterBar}>
        <button onClick={load} disabled={busy} style={S.btn}>{busy ? "Loading…" : "Refresh"}</button>
        <div style={{ ...ts.small, alignSelf: "center" }}>Hard cap <b style={{ color: "#a5ce3a" }}>${data?.cap ?? 25}/day</b> · changes are live in Meta, logged &amp; reversible</div>
      </div>
      {msg && <div style={S.statusBanner}>{msg}<button style={S.bannerClose} onClick={() => setMsg("")}>×</button></div>}
      {err && <div style={S.errorBanner}>{err}<button style={S.bannerClose} onClick={() => setErr("")}>×</button></div>}

      {/* Guardian: auto-flagged waste with one-click pause */}
      {flags.length > 0 && (
        <div>
          <div style={{ color: "#e8eaed", fontSize: 16, margin: "6px 0" }}>⚠️ Guardian flags ({flags.length}) — campaigns wasting spend (last 14d)</div>
          {flags.map((f, i) => (
            <div key={i} style={{ ...ts.card, borderColor: f.severity === "high" ? "rgba(226,109,109,0.4)" : "#2a2e35", background: f.severity === "high" ? "rgba(226,109,109,0.08)" : "#1c1f24", display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ color: "#e8eaed", fontWeight: 600 }}>{f.name}</div>
                <div style={ts.small}>{f.account} · <span style={{ color: f.severity === "high" ? "#e26d6d" : "#d9a441" }}>{f.flag}</span>{f.auto_paused ? " · auto-paused ✓" : ""}</div>
              </div>
              <button onClick={() => setStatus({ external_id: f.external_id, name: f.name, status: "ACTIVE" }, "PAUSED")} disabled={applying === f.external_id} style={S.btn}>Pause</button>
            </div>
          ))}
        </div>
      )}

      {data?.campaigns?.map(c => c.error ? (
        <div key={c.external_account_id} style={{ ...ts.card, borderColor: "#a5701a" }}>
          <div style={{ color: "#d9a441" }}>{c.account}: {c.error}</div>
        </div>
      ) : (
        <div key={c.external_id} style={ts.card}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ color: "#e8eaed", fontWeight: 600 }}>{c.name}</div>
              <div style={ts.small}>{c.account} · {c.objective || "—"} · {c.daily_budget != null ? `$${c.daily_budget}/day` : (c.lifetime_budget != null ? `$${c.lifetime_budget} lifetime` : "no budget set")}</div>
            </div>
            <div style={{ fontSize: 12, padding: "2px 10px", borderRadius: 12, background: c.effective_status === "ACTIVE" ? "rgba(46,125,50,0.2)" : "#2a2e35", color: c.effective_status === "ACTIVE" ? "#69b36b" : "#c5cad1" }}>{c.effective_status || c.status}</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              {c.status === "ACTIVE"
                ? <button onClick={() => setStatus(c, "PAUSED")} disabled={applying === c.external_id} style={S.btn}>Pause</button>
                : <button onClick={() => setStatus(c, "ACTIVE")} disabled={applying === c.external_id} style={S.btnPrimary}>Activate</button>}
              <input type="number" placeholder="$/day" value={budgets[c.external_id] ?? ""} onChange={e => setBudgets(b => ({ ...b, [c.external_id]: e.target.value }))} style={{ ...S.input, width: 80 }} />
              <button onClick={() => applyBudget(c)} disabled={applying === c.external_id} style={S.btn}>Set budget</button>
              <button onClick={() => toggleAutopilot(c)} disabled={applying === c.external_id} style={c.autopilot ? S.btnPrimary : S.btn} title="Let the guardian auto-pause this campaign if it genuinely wastes money">{c.autopilot ? "🤖 Autopilot ON" : "Autopilot off"}</button>
            </div>
          </div>
        </div>
      ))}
      {data && !data.campaigns?.length && <div style={ts.small}>No campaigns returned.</div>}

      <div>
        <div style={{ color: "#e8eaed", fontSize: 16, margin: "6px 0" }}>Recent changes ({actions.length})</div>
        {actions.length === 0 && <div style={ts.small}>No changes yet.</div>}
        {actions.map(a => (
          <div key={a.id} style={{ ...ts.card, display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ color: "#e8eaed" }}>{a.action} · {a.campaign_name || a.external_id}</div>
              <div style={ts.small}>{a.created_at ? String(a.created_at).slice(0, 19).replace("T", " ") : ""} · by {a.requested_by || "—"} · {a.status}{a.note ? ` · ${a.note}` : ""}</div>
            </div>
            {a.status === "applied" && <button onClick={() => revert(a.id)} style={S.btn}>Revert</button>}
          </div>
        ))}
      </div>
    </div>
  );
}

// Ad Builder: turn top-performing organic posts into Meta lead-gen ad DRAFTS
// (AI-written), review them, approve, and push to Meta as PAUSED. Never spends
// without a human. Organic-post reading is dormant until Page/IG perms granted.
function AdBuilderTab() {
  const perms = usePermissions() || {};
  const canSeeFinance = !!(perms.isAdmin || perms.isOwner || perms.hasPermission?.("view_marketing_finance"));
  const [posts, setPosts]   = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [busy, setBusy]     = useState(false);
  const [genId, setGenId]   = useState(null);
  const [cat, setCat]       = useState("generator");
  const [msg, setMsg]       = useState("");
  const [err, setErr]       = useState("");

  const MS = "/api/meta-sync";
  const ts = {
    card: { background: "#1c1f24", border: "1px solid #2a2e35", borderRadius: 8, padding: 14, marginBottom: 10 },
    h3: { margin: "6px 0", color: "#e8eaed", fontSize: 16 },
    small: { fontSize: 12, color: "#9aa0a6" },
    lbl: { fontSize: 11, color: "#9aa0a6", textTransform: "uppercase", letterSpacing: 0.4, marginTop: 6 },
    val: { color: "#e8eaed", fontSize: 14, whiteSpace: "pre-wrap" },
  };

  const loadDrafts = useCallback(async () => {
    try {
      const r = await apiFetch(`${MS}?action=list-drafts`).then(r => r.json());
      if (r.ok) setDrafts(r.drafts || []);
    } catch { /* ignore */ }
  }, []);
  const loadPosts = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const r = await apiFetch(`${MS}?action=top-posts&limit=12`).then(r => r.json());
      if (!r.ok) throw new Error(r.error || "top-posts failed");
      setPosts(r);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }, []);
  useEffect(() => { loadPosts(); loadDrafts(); }, [loadPosts, loadDrafts]);

  const generate = useCallback(async (post) => {
    setGenId(post ? post.id : "scratch"); setMsg(""); setErr("");
    try {
      const r = await apiFetch(`${MS}?action=generate-draft`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: post?.message || "", platform: post?.platform || null,
          permalink: post?.permalink || null, service_category: cat,
        }),
      });
      const b = await r.json();
      if (!r.ok || !b.ok) throw new Error(b.error || `HTTP ${r.status}`);
      setMsg(`Draft created: "${b.draft?.hook || b.draft?.campaign_idea || "new ad"}" — review it below.`);
      await loadDrafts();
    } catch (e) { setErr(e.message); } finally { setGenId(null); }
  }, [cat, loadDrafts]);

  const setStatus = useCallback(async (id, status, reason) => {
    try {
      const r = await apiFetch(`${MS}?action=set-draft-status`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, status, reason }),
      });
      const b = await r.json();
      if (!r.ok || !b.ok) throw new Error(b.error || `HTTP ${r.status}`);
      await loadDrafts();
    } catch (e) { setErr(e.message); }
  }, [loadDrafts]);

  const push = useCallback(async (id) => {
    setMsg(""); setErr("");
    try {
      const r = await apiFetch(`${MS}?action=push-draft&draft_id=${id}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: "{}" });
      const b = await r.json();
      if (!r.ok || !b.ok) throw new Error(b.error || `HTTP ${r.status}`);
      setMsg("Pushed to Meta as PAUSED — review & activate in Ads Manager.");
      await loadDrafts();
    } catch (e) { setErr(`Push failed: ${e.message}`); }
  }, [loadDrafts]);

  if (!canSeeFinance) return <div style={{ padding: 20, color: "#9aa0a6" }}>Ad Builder requires marketing-finance access.</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={S.filterBar}>
        <FilterField label="Service for new ads">
          <select value={cat} onChange={e => setCat(e.target.value)} style={S.input}>
            {["generator", "solar", "ev_charger", "panel_upgrade", "service", "lighting"].map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </FilterField>
        <button onClick={loadPosts} disabled={busy} style={S.btn}>{busy ? "Loading…" : "Refresh posts"}</button>
        <button onClick={() => generate(null)} disabled={genId === "scratch"} style={S.btnPrimary}>{genId === "scratch" ? "Writing…" : "Generate ad from scratch"}</button>
      </div>
      {msg && <div style={S.statusBanner}>{msg}<button style={S.bannerClose} onClick={() => setMsg("")}>×</button></div>}
      {err && <div style={S.errorBanner}>{err}<button style={S.bannerClose} onClick={() => setErr("")}>×</button></div>}

      {/* Top organic posts */}
      <div>
        <div style={ts.h3}>Your best organic posts</div>
        {posts?.needs_permission && (
          <div style={{ ...ts.card, borderColor: "#a5701a", background: "rgba(165,112,26,0.10)" }}>
            <div style={{ color: "#d9a441", fontWeight: 600 }}>Organic posts not connected yet</div>
            <div style={ts.small}>{posts.note}</div>
          </div>
        )}
        {posts?.posts?.length > 0 && posts.posts.map(p => (
          <div key={p.id} style={ts.card}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={ts.small}>{p.platform} · {p.page} · {p.created_time ? String(p.created_time).slice(0, 10) : ""}</div>
                <div style={{ ...ts.val, marginTop: 4 }}>{(p.message || "").slice(0, 300)}{(p.message || "").length > 300 ? "…" : ""}</div>
                <div style={{ ...ts.small, marginTop: 6 }}>👍 {p.reactions} · 💬 {p.comments} · 🔁 {p.shares} · <b style={{ color: "#a5ce3a" }}>engagement {p.engagement}</b></div>
              </div>
              <div>
                <button onClick={() => generate(p)} disabled={genId === p.id} style={S.btnPrimary}>{genId === p.id ? "Writing…" : "Generate ad from this"}</button>
              </div>
            </div>
          </div>
        ))}
        {posts && !posts.needs_permission && !(posts.posts?.length) && <div style={ts.small}>No organic posts found.</div>}
      </div>

      {/* Draft queue */}
      <div>
        <div style={ts.h3}>Ad drafts ({drafts.length}) — review → approve → push PAUSED</div>
        {drafts.length === 0 && <div style={ts.small}>No drafts yet. Generate one from a post above.</div>}
        {drafts.map(d => (
          <div key={d.id} style={ts.card}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
              <div style={{ fontWeight: 700, color: "#e8eaed" }}>{d.campaign_idea || d.headline || "Untitled draft"}</div>
              <div style={{ fontSize: 12, padding: "2px 8px", borderRadius: 12, background: d.status === "approved" ? "rgba(46,125,50,0.2)" : d.status === "rejected" ? "rgba(226,109,109,0.15)" : "#2a2e35", color: d.status === "approved" ? "#69b36b" : d.status === "rejected" ? "#e26d6d" : "#c5cad1" }}>{d.status}</div>
            </div>
            <div style={ts.lbl}>Hook</div><div style={ts.val}>{d.hook || "—"}</div>
            <div style={ts.lbl}>Primary text</div><div style={ts.val}>{d.primary_text || "—"}</div>
            <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
              <div><div style={ts.lbl}>Headline</div><div style={ts.val}>{d.headline || "—"}</div></div>
              <div><div style={ts.lbl}>CTA</div><div style={ts.val}>{d.cta || "—"}</div></div>
              <div><div style={ts.lbl}>Service</div><div style={ts.val}>{d.service_category || "—"}</div></div>
            </div>
            <div style={ts.lbl}>Targeting</div><div style={ts.val}>{d.targeting_notes || "—"}</div>
            <div style={ts.lbl}>Budget</div><div style={ts.val}>{d.budget_recommendation || "—"}</div>
            {Array.isArray(d.lead_form_questions) && d.lead_form_questions.length > 0 && (
              <>
                <div style={ts.lbl}>Lead-form questions</div>
                <div style={ts.val}>{d.lead_form_questions.map((q, i) => `${i + 1}. ${q}`).join("\n")}</div>
              </>
            )}
            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              {d.status !== "approved" && <button onClick={() => setStatus(d.id, "approved")} style={S.btnPrimary}>Approve</button>}
              {d.status !== "rejected" && <button onClick={() => setStatus(d.id, "rejected")} style={S.btn}>Reject</button>}
              {d.status === "approved" && <button onClick={() => push(d.id)} style={S.btn} title="Creates the campaign/adset/ad on Meta with status PAUSED">Push to Meta (PAUSED)</button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OverviewTab() {
  const perms = usePermissions() || {};
  const canSeeFinance = !!(
    perms.isAdmin || perms.isOwner || perms.hasPermission?.("view_marketing_finance")
  );

  const [range, setRange]                       = useState(defaultRange());
  const [serviceCategory, setServiceCategory]   = useState("all");
  const [sourceId, setSourceId]                 = useState("all");
  const [sources, setSources]                   = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);

  const [data, setData]   = useState(null);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState("");
  const [syncStatus, setSyncStatus] = useState("");
  const [syncBusy,   setSyncBusy]   = useState(false);

  // Load sources + service-category options for the filter dropdowns. We
  // swallow errors silently because the marketing_* tables may not exist
  // yet on a fresh project — the page still works with the data it has.
  useEffect(() => {
    (async () => {
      try {
        const { data: rows, error: e } = await supabase
          .from("marketing_sources")
          .select("id,name,platform,agency_name")
          .eq("status", "active")
          .order("name");
        if (e) return;
        setSources(rows || []);
      } catch { /* table may not exist yet */ }
    })();
    (async () => {
      try {
        const { data: rows, error: e } = await supabase
          .from("marketing_campaigns")
          .select("service_category")
          .not("service_category", "is", null);
        if (e) return;
        const set = new Set();
        for (const r of rows || []) if (r.service_category) set.add(r.service_category);
        setServiceCategories([...set].sort());
      } catch { /* table may not exist yet */ }
    })();
  }, []);

  const load = useCallback(async () => {
    setBusy(true); setError("");
    try {
      const params = new URLSearchParams({
        start: range.start, end: range.end,
        service_category: serviceCategory,
        source_id: sourceId,
      });
      const r = await apiFetch(`${API}?action=overview&${params}`).then(r => r.json());
      if (!r.ok) throw new Error(r.error || "Overview failed");
      setData(r);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }, [range.start, range.end, serviceCategory, sourceId]);

  useEffect(() => { load(); }, [load]);

  const syncMeta = useCallback(async (days = 30) => {
    setSyncBusy(true); setSyncStatus(""); setError("");
    try {
      const r = await apiFetch(`/api/meta-sync?action=sync&days=${days}`, { method: "POST" });
      const body = await r.json();
      if (!r.ok || !body.ok) throw new Error(body.error || `HTTP ${r.status}`);
      // Build a one-line summary across both accounts
      const parts = (body.results || []).map(res => {
        if (!res.ok) return `${res.source} ✗ ${res.error}`;
        const c   = res.campaigns?.count ?? 0;
        const a   = res.adsets_ads?.ads ?? 0;
        const ins = res.insights?.rows ?? 0;
        const lds = res.leads?.leads ?? 0;
        const skipped = res.leads?.skipped ? ` (leads: ${res.leads.skipped})` : "";
        return `${res.source}: ${c} camp · ${a} ads · ${ins} daily · ${lds} leads${skipped}`;
      });
      setSyncStatus(`Synced in ${Math.round(body.elapsed_ms / 100) / 10}s — ${parts.join(" | ")}`);
      await load();
    } catch (e) {
      setError(`Meta sync failed: ${e.message}`);
    } finally {
      setSyncBusy(false);
    }
  }, [load]);

  return (
    <>
      {canSeeFinance && <MetaConnectionCard />}
      <div style={S.filterBar}>
        <FilterField label="Start">
          <input type="date" value={range.start} onChange={e => setRange({ ...range, start: e.target.value })} style={S.input} />
        </FilterField>
        <FilterField label="End">
          <input type="date" value={range.end} onChange={e => setRange({ ...range, end: e.target.value })} style={S.input} />
        </FilterField>
        <FilterField label="Service Category">
          <select value={serviceCategory} onChange={e => setServiceCategory(e.target.value)} style={S.input}>
            <option value="all">All</option>
            {serviceCategories.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </FilterField>
        <FilterField label="Source / Agency">
          <select value={sourceId} onChange={e => setSourceId(e.target.value)} style={S.input}>
            <option value="all">All</option>
            {sources.map(s => (
              <option key={s.id} value={s.id}>
                {s.name}{s.agency_name ? ` (${s.agency_name})` : ""}
              </option>
            ))}
          </select>
        </FilterField>
        <button onClick={load} disabled={busy} style={S.btnPrimary}>
          {busy ? "Loading…" : "Apply"}
        </button>
        <QuickRangeButtons onPick={r => setRange(r)} />
        <div style={{ flex: 1 }} />
        <button onClick={() => syncMeta(30)} disabled={syncBusy} style={S.btn} title="Pull campaigns, ads, insights, and leads from Meta for the last 30 days">
          {syncBusy ? "Syncing Meta…" : "Sync Meta Ads (30d)"}
        </button>
        <button onClick={() => syncMeta(90)} disabled={syncBusy} style={S.btn} title="Backfill the last 90 days from Meta">
          Backfill 90d
        </button>
      </div>

      {syncStatus && <div style={S.statusBanner}>{syncStatus}<button style={S.bannerClose} onClick={() => setSyncStatus("")}>×</button></div>}
      {error && <div style={S.errorBanner}>{error}<button style={S.bannerClose} onClick={() => setError("")}>×</button></div>}

      {/* Recommended action summary */}
      {data?.recommended_actions?.length > 0 && (
        <RecommendationsCard recs={data.recommended_actions} />
      )}

      {/* Data warnings */}
      {data?.data_warnings?.length > 0 && (
        <DataWarningsBanner warnings={data.data_warnings} />
      )}

      {/* Metric cards */}
      {data?.metrics && (
        <div style={S.kpiGrid}>
          {Object.entries(data.metrics).map(([key, m]) => {
            const isFinance = FINANCE_METRIC_KEYS.has(key);
            const hideValue = isFinance && !canSeeFinance;
            return (
              <Kpi
                key={key}
                label={m.label}
                value={hideValue ? "Hidden" : fmtMetric(m)}
                sub={hideValue ? "view_marketing_finance required" : undefined}
                highlight={hideValue ? undefined : highlightForMetric(key, m.value)}
              />
            );
          })}
        </div>
      )}

      {/* Footer — spend source + match coverage */}
      {data && (
        <div style={{ fontSize: 11, color: "#8892a4", marginTop: 8, padding: "0 4px" }}>
          Spend source:{" "}
          <strong style={{ color: "#cdd2dd" }}>
            {data.spend_source === "marketing_daily_metrics"
              ? "marketing_daily_metrics"
              : "QuickBooks (account 6203)"}
          </strong>
          {data.counts && (
            <>
              {" · "}
              {data.counts.daily_metric_rows} daily metric row{data.counts.daily_metric_rows === 1 ? "" : "s"}
              {" · "}
              {data.counts.leads} lead{data.counts.leads === 1 ? "" : "s"} ({data.counts.unmatched_leads} unmatched)
            </>
          )}
        </div>
      )}

      {!data && !busy && !error && (
        <div style={S.card}><div style={S.emptyText}>Loading marketing overview…</div></div>
      )}
    </>
  );
}

function RecommendationsCard({ recs }) {
  return (
    <div style={S.card}>
      <div style={S.cardHeader}>
        <div>
          <div style={S.cardTitle}>Recommended actions</div>
          <div style={S.cardSubtitle}>
            {recs.length} open recommendation{recs.length === 1 ? "" : "s"} — highest priority first.
          </div>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column" }}>
        {recs.map(r => <RecRow key={r.id} rec={r} />)}
      </div>
    </div>
  );
}

const REC_TYPE_BADGES = {
  scale:       { style: { ...{}, background: "rgba(34,197,94,0.15)",  color: "#86efac", border: "1px solid rgba(34,197,94,0.3)"  }, text: "SCALE" },
  watch:       { style: { ...{}, background: "rgba(165,206,58,0.12)", color: "#a5ce3a", border: "1px solid rgba(165,206,58,0.3)" }, text: "WATCH" },
  fix:         { style: { ...{}, background: "rgba(245,158,11,0.15)", color: "#fcd34d", border: "1px solid rgba(245,158,11,0.3)" }, text: "FIX" },
  pause:       { style: { ...{}, background: "rgba(239,68,68,0.15)",  color: "#fca5a5", border: "1px solid rgba(239,68,68,0.3)"  }, text: "PAUSE" },
  test:        { style: { ...{}, background: "rgba(165,206,58,0.12)", color: "#a5ce3a", border: "1px solid rgba(165,206,58,0.3)" }, text: "TEST" },
  investigate: { style: { ...{}, background: "rgba(245,158,11,0.15)", color: "#fcd34d", border: "1px solid rgba(245,158,11,0.3)" }, text: "LOOK" },
};

function RecRow({ rec }) {
  const badge = REC_TYPE_BADGES[rec.type] || {
    style: { background: "rgba(255,255,255,0.05)", color: "#8892a4", border: "1px solid rgba(255,255,255,0.08)" },
    text:  (rec.type || "?").toUpperCase(),
  };
  const priorityColor =
    rec.priority === "high"   ? "#fca5a5" :
    rec.priority === "medium" ? "#fcd34d" : "#8892a4";

  return (
    <div style={{
      display: "flex", gap: 12, alignItems: "flex-start",
      padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.04)"
    }}>
      <span style={{
        display: "inline-block", padding: "2px 8px", borderRadius: 999,
        fontSize: 10, fontWeight: 700, letterSpacing: "0.04em",
        textTransform: "uppercase", whiteSpace: "nowrap",
        ...badge.style,
      }}>{badge.text}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, color: "#e8eaf0", fontSize: 13 }}>{rec.title}</div>
        {rec.explanation && (
          <div style={{ fontSize: 12, color: "#cdd2dd", marginTop: 4, lineHeight: 1.5 }}>{rec.explanation}</div>
        )}
      </div>
      <div style={{
        fontSize: 10, color: priorityColor, textTransform: "uppercase",
        fontWeight: 700, letterSpacing: "0.05em",
      }}>{rec.priority}</div>
    </div>
  );
}

function DataWarningsBanner({ warnings }) {
  return (
    <div style={{
      background: "rgba(245,158,11,0.08)",
      border: "1px solid rgba(245,158,11,0.25)",
      color: "#fcd34d",
      padding: "10px 14px", borderRadius: 8, marginBottom: 12, fontSize: 13,
    }}>
      <div style={{
        fontWeight: 700, marginBottom: 6, fontSize: 11,
        textTransform: "uppercase", letterSpacing: "0.04em",
      }}>Data gaps</div>
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {warnings.map((w, i) => <li key={i} style={{ marginBottom: 4 }}>{w}</li>)}
      </ul>
    </div>
  );
}

// ── Funnel tab body (everything that used to be inline in Marketing) ──────
function FunnelTabContent({
  range, setRange, dateBasis, setDateBasis,
  serviceType, setServiceType, serviceTypes,
  leadSource, setLeadSource, leadSources,
  keyword, setKeyword, loadData, busy,
  funnel, tableData, sortedRows, shownColumns,
  toggleCol, sortBy, sortIndicator, footer,
}) {
  return (
    <>
      {/* Filters */}
      <div style={S.filterBar}>
        <FilterField label="Start">
          <input type="date" value={range.start} onChange={e => setRange({ ...range, start: e.target.value })} style={S.input} />
        </FilterField>
        <FilterField label="End">
          <input type="date" value={range.end} onChange={e => setRange({ ...range, end: e.target.value })} style={S.input} />
        </FilterField>
        <FilterField label="Filter dates by">
          <select value={dateBasis} onChange={e => setDateBasis(e.target.value)} style={S.input}>
            {DATE_BASIS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </FilterField>
        <FilterField label="Service Type">
          <select value={serviceType} onChange={e => setServiceType(e.target.value)} style={S.input}>
            <option value="all">All ({serviceTypes.length})</option>
            {serviceTypes.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </FilterField>
        <FilterField label="Lead Source">
          <select value={leadSource} onChange={e => setLeadSource(e.target.value)} style={S.input}>
            <option value="all">All ({leadSources.length})</option>
            {leadSources.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </FilterField>
        <FilterField label="Search (title, description, line items)">
          <input type="text" placeholder='e.g. "generator" or "service upgrade"' value={keyword} onChange={e => setKeyword(e.target.value)} style={{ ...S.input, minWidth: 220 }} />
        </FilterField>
        <button onClick={loadData} disabled={!!busy} style={S.btnPrimary}>
          {busy === "loading" ? "Loading…" : "Apply"}
        </button>
        <QuickRangeButtons onPick={r => setRange(r)} />
      </div>

      {/* Data warnings (e.g. lead history doesn't cover the selected window) */}
      {funnel?.data_warnings?.length > 0 && funnel.data_warnings.map((w, i) => (
        <div key={i} style={{ ...S.card, borderLeft: "3px solid #f59e0b", padding: "10px 14px", fontSize: 12.5, color: "#cbb27a" }}>
          ⚠ {w}
        </div>
      ))}

      {/* Funnel KPIs */}
      {funnel && (
        <FunnelStrip funnel={funnel} />
      )}

      {/* Marketing spend block */}
      {funnel?.marketing_spend && (
        <MarketingSpendCard spend={funnel.marketing_spend} costPer={funnel.cost_per} roas={funnel.roas} />
      )}

      {/* Detail table */}
      {tableData && (
        <DetailTable
          rows={sortedRows}
          turnaround={tableData.turnaround}
          shownColumns={shownColumns}
          toggleCol={toggleCol}
          sortBy={sortBy}
          sortIndicator={sortIndicator}
          footer={footer}
        />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Google Ads tab — search campaigns + LSA leads, cost per booked job
// Data: /api/google-ads?action=overview (marketing_daily_metrics +
// marketing_leads under the two Google sources, joined to jb_requests/jb_quotes)
// ═══════════════════════════════════════════════════════════════════════════
function GoogleAdsTab() {
  const perms = usePermissions() || {};
  const canFinance = !!(perms.isAdmin || perms.isOwner || perms.hasPermission?.("view_marketing_finance"));
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");

  // Monotonic sequence so a slow older fetch can't clobber a newer range's data
  const loadSeq = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setBusy(true); setError("");
    try {
      const r = await apiFetch(`/api/google-ads?action=overview&days=${days}`).then((x) => x.json());
      if (!r.ok) throw new Error(r.error || "Failed to load Google Ads data");
      if (seq !== loadSeq.current) return;
      setData(r);
    } catch (e) { if (seq === loadSeq.current) setError(e.message); }
    if (seq === loadSeq.current) setBusy(false);
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const runSync = async () => {
    setBusy(true); setError(""); setStatus("");
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      const r = await apiFetch(`/api/google-ads?action=cron`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }).then((x) => x.json());
      if (!r.ok) throw new Error(r.error || "Sync failed");
      const bits = [];
      if (r.results?.lsa?.ok) bits.push(`${r.results.lsa.new_leads || 0} LSA leads`);
      if (r.results?.report?.ok) bits.push(`${r.results.report.ingested || 0} report emails`);
      if (r.results?.match?.ok) bits.push(`${r.results.match.matched || 0} clicks matched`);
      if (r.results?.conversions?.ok) bits.push(`${r.results.conversions.appended || 0} conversions exported`);
      setStatus(`Sync done — ${bits.join(", ") || "no new data"}`);
      await load();
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const wowPct = (cur, prev) => {
    if (!prev) return cur ? "new" : "—";
    const p = Math.round(100 * (cur - prev) / prev);
    return `${p >= 0 ? "+" : ""}${p}% WoW`;
  };

  if (!data && busy) return <div style={S.emptyText}>Loading Google Ads data…</div>;
  if (!data) return (
    <div>
      {error && <div style={S.errorBanner}>{error}</div>}
      <div style={S.emptyText}>No Google Ads data yet.</div>
    </div>
  );

  const t = data.totals || {};
  const wow = data.wow || { this_week: {}, last_week: {} };
  const lsa = data.lsa || {};
  const attrib = data.attribution || {};
  const conv = data.conversions || {};
  // Normalize bars against only the weeks actually rendered
  const lsaWeeklyShown = (lsa.weekly || []).slice(-8);
  const maxWeekly = Math.max(1, ...lsaWeeklyShown.map((w) => w.count));

  return (
    <div>
      {error && <div style={S.errorBanner}>{error}<button style={S.bannerClose} onClick={() => setError("")}>×</button></div>}
      {status && <div style={S.statusBanner}>{status}<button style={S.bannerClose} onClick={() => setStatus("")}>×</button></div>}

      <div style={S.filterBar}>
        {[30, 90, 180].map((d) => (
          <button key={d} onClick={() => setDays(d)}
            style={{ ...S.chip, ...(days === d ? { color: "#a5ce3a", borderColor: "rgba(165,206,58,0.4)" } : {}) }}>
            {d}d
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button style={S.btnPrimary} onClick={runSync} disabled={busy}>
          {busy ? "Working…" : "Sync now"}
        </button>
      </div>

      {!data.has_metrics && (
        <div style={S.card}>
          <div style={S.cardTitle}>⚙️ Spend data not flowing yet</div>
          <div style={{ ...S.cardSubtitle, lineHeight: 1.6 }}>
            No daily campaign stats have been ingested. Set up the scheduled email report in
            Google Ads (report name must contain &quot;ElecBid&quot;) — steps in <code style={{ color: "#a5ce3a" }}>GOOGLE_ADS_SETUP.md</code> at
            the repo root. Leads and attribution below still work without it.
          </div>
        </div>
      )}

      <div style={S.kpiGrid}>
        <Kpi label="Spend" value={fmtMoney(t.spend)} sub={wowPct(wow.this_week.spend, wow.last_week.spend)} />
        <Kpi label="Clicks" value={(t.clicks || 0).toLocaleString()} sub={wowPct(wow.this_week.clicks, wow.last_week.clicks)} />
        <Kpi label="Form conversions" value={t.platform_conversions || 0} sub="Google-reported" />
        <Kpi label="Website leads" value={t.leads || 0} sub={wowPct(wow.this_week.leads, wow.last_week.leads)} />
        <Kpi label="Booked jobs" value={t.booked || 0}
          highlight={t.booked > 0 ? "good" : undefined}
          sub={wowPct(wow.this_week.booked, wow.last_week.booked)} />
        <Kpi label="Cost / lead" value={t.cost_per_lead != null ? fmtMoney2(t.cost_per_lead) : "—"}
          highlight={t.cost_per_lead == null ? undefined : t.cost_per_lead <= 60 ? "good" : t.cost_per_lead <= 120 ? "ok" : "bad"} />
        <Kpi label="Cost / booked job" value={t.cost_per_booked_job != null ? fmtMoney2(t.cost_per_booked_job) : "—"}
          sub="the number that matters" />
        {canFinance && (
          <Kpi label="Approved revenue" value={fmtMoney(t.revenue)}
            highlight={t.revenue > t.spend * 3 ? "good" : t.revenue > 0 ? "ok" : undefined}
            sub={t.spend > 0 && t.revenue > 0 ? `${(t.revenue / t.spend).toFixed(1)}× spend` : "from won quotes"} />
        )}
      </div>

      <div style={S.card}>
        <div style={S.cardHeader}>
          <div>
            <div style={S.cardTitle}>Search campaigns — last {data.days} days</div>
            <div style={S.cardSubtitle}>
              &quot;Leads&quot; = website requests attributed to Google (click beacon or Jobber utm tag).
              &quot;Booked&quot; = those requests that became Jobber jobs.
            </div>
          </div>
        </div>
        {(data.campaigns || []).length === 0 ? (
          <div style={S.emptyText}>No campaign data yet — run a sync or set up the email report.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={{ ...S.th, textAlign: "left" }}>Campaign</th>
                  <th style={S.th}>Spend</th>
                  <th style={S.th}>Clicks</th>
                  <th style={S.th}>CTR</th>
                  <th style={S.th}>CPC</th>
                  <th style={S.th}>Conv.</th>
                  <th style={S.th}>Leads</th>
                  <th style={S.th}>Booked</th>
                  <th style={S.th}>$/Lead</th>
                  <th style={S.th}>$/Booked</th>
                  {canFinance && <th style={S.th}>Revenue</th>}
                </tr>
              </thead>
              <tbody>
                {data.campaigns.map((c) => (
                  <tr key={c.campaign_id || "none"}>
                    <td style={S.tdLabel}>
                      {c.name}
                      {c.status === "paused" && <span style={{ ...S.badge, ...S.badgeMuted, marginLeft: 6 }}>paused</span>}
                    </td>
                    <td style={S.td}>{fmtMoney2(c.spend)}</td>
                    <td style={S.td}>{c.clicks.toLocaleString()}</td>
                    <td style={S.tdMuted}>{c.ctr != null ? `${c.ctr}%` : "—"}</td>
                    <td style={S.tdMuted}>{c.cpc != null ? fmtMoney2(c.cpc) : "—"}</td>
                    <td style={S.tdMuted}>{c.platform_conversions || 0}</td>
                    <td style={S.td}>{c.leads || 0}</td>
                    <td style={{ ...S.td, color: c.booked ? "#86efac" : "#8892a4" }}>{c.booked || 0}</td>
                    <td style={S.tdMuted}>{c.cost_per_lead != null ? fmtMoney2(c.cost_per_lead) : "—"}</td>
                    <td style={S.td}>{c.cost_per_booked_job != null ? fmtMoney2(c.cost_per_booked_job) : "—"}</td>
                    {canFinance && <td style={S.td}>{c.revenue ? fmtMoney(c.revenue) : "—"}</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 16 }}>
        <div style={{ ...S.card, marginBottom: 0 }}>
          <div style={S.cardTitle}>Local Services Ads — last {data.days} days</div>
          <div style={S.cardSubtitle}>
            {lsa.leads || 0} leads · {lsa.enriched || 0} matched to a caller via the phone log
            {lsa.matched_to_job ? ` · ${lsa.matched_to_job} became jobs` : ""}
          </div>
          <div style={{ marginTop: 12 }}>
            {lsaWeeklyShown.length === 0 ? (
              <div style={S.emptyText}>No LSA leads ingested yet.</div>
            ) : (
              lsaWeeklyShown.map((w) => (
                <div key={w.week} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <div style={{ fontSize: 11, color: "#8892a4", width: 76, flexShrink: 0 }}>wk {w.week.slice(5)}</div>
                  <div style={{ flex: 1, height: 14, background: "rgba(255,255,255,0.04)", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ width: `${Math.round(100 * w.count / maxWeekly)}%`, height: "100%", background: "rgba(165,206,58,0.55)", borderRadius: 4 }} />
                  </div>
                  <div style={{ fontSize: 12, color: "#e8eaf0", width: 24, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{w.count}</div>
                </div>
              ))
            )}
          </div>
        </div>

        <div style={{ ...S.card, marginBottom: 0 }}>
          <div style={S.cardTitle}>Attribution health</div>
          <div style={{ marginTop: 10, fontSize: 13, lineHeight: 2, color: "#cdd2dd" }}>
            <div>
              Site click beacons: <b>{attrib.beacons || 0}</b>
              {!attrib.snippet_installed && (
                <span style={{ ...S.badge, ...S.badgeWarn, marginLeft: 8 }}>snippet not installed</span>
              )}
            </div>
            <div>Beacons matched to a request: <b>{attrib.beacons_matched || 0}</b></div>
            <div>Beacons carrying a gclid: <b>{attrib.beacons_with_gclid || 0}</b></div>
          </div>
          {!attrib.snippet_installed && (
            <div style={{ ...S.cardSubtitle, marginTop: 10, lineHeight: 1.6 }}>
              Install the GTM snippet from <code style={{ color: "#a5ce3a" }}>GOOGLE_ADS_SETUP.md</code> on
              browning-electric.ca to start capturing clicks — without it only Jobber&apos;s own
              utm_source tag attributes leads (no gclid, no campaign detail).
            </div>
          )}
        </div>

        <div style={{ ...S.card, marginBottom: 0 }}>
          <div style={S.cardTitle}>Offline conversions → Google Ads</div>
          <div style={{ marginTop: 10, fontSize: 13, lineHeight: 2, color: "#cdd2dd" }}>
            <div>
              Sheet configured:{" "}
              {conv.sheet_configured
                ? <span style={{ ...S.badge, ...S.badgeGood }}>yes</span>
                : <span style={{ ...S.badge, ...S.badgeWarn }}>no — set ADS_CONVERSION_SHEET_ID</span>}
            </div>
            <div>Pending rows: <b>{conv.pending || 0}</b></div>
            <div>Exported: <b>{conv.appended || 0}</b>{canFinance && conv.appended_value ? ` (${fmtMoney(conv.appended_value)})` : ""}</div>
            {conv.last_appended_at && <div style={{ color: "#8892a4", fontSize: 12 }}>Last export {fmtDate(conv.last_appended_at)}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Filter field wrapper ──────────────────────────────────────────────────
function FilterField({ label, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 11, color: "#8892a4", textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</label>
      {children}
    </div>
  );
}

function QuickRangeButtons({ onPick }) {
  const today = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  const ranges = [
    { label: "30d",   days: 30 },
    { label: "90d",   days: 90 },
    { label: "MTD",   special: "mtd" },
    { label: "YTD",   special: "ytd" },
  ];
  const compute = (r) => {
    const end = today;
    if (r.special === "mtd") {
      return { start: fmt(new Date(today.getFullYear(), today.getMonth(), 1)), end: fmt(end) };
    }
    if (r.special === "ytd") {
      // Fiscal year June 1 → May 31
      const fyStartYear = today.getMonth() + 1 >= 6 ? today.getFullYear() : today.getFullYear() - 1;
      return { start: fmt(new Date(fyStartYear, 5, 1)), end: fmt(end) };
    }
    return { start: fmt(new Date(today.getTime() - r.days * 24 * 60 * 60 * 1000)), end: fmt(end) };
  };
  return (
    <div style={{ display: "flex", gap: 4, alignItems: "flex-end" }}>
      {ranges.map(r => (
        <button key={r.label} onClick={() => onPick(compute(r))} style={S.chip}>{r.label}</button>
      ))}
    </div>
  );
}

// ── Funnel KPI strip ──────────────────────────────────────────────────────
function FunnelStrip({ funnel }) {
  const { counts, money, win_rate } = funnel;
  return (
    <div style={S.kpiGrid}>
      <Kpi label="Requests"     value={counts.requests} />
      <Kpi label="Quotes Sent"  value={counts.sent} sub={fmtMoney(money.quote_value_sent)} />
      <Kpi label="Approved"     value={counts.approved}
           sub={`${fmtMoney(money.quote_value_approved)} · win rate ${fmtPct(win_rate)}`}
           highlight={win_rate >= 50 ? "good" : win_rate >= 30 ? "ok" : "bad"} />
      <Kpi label="Lost"         value={counts.archived_lost} sub="archived without approval" />
      <Kpi label="Jobs Closed"  value={counts.jobs_completed} />
      <Kpi label="Invoiced"     value={counts.invoiced} sub={`${fmtMoney(money.revenue)} revenue`} />
      <Kpi label="Gross Profit" value={fmtMoney(money.profit)} sub={fmtPct(money.profit_pct)} />
    </div>
  );
}

// ── Marketing spend card ──────────────────────────────────────────────────
function MarketingSpendCard({ spend, costPer, roas }) {
  return (
    <div style={S.card}>
      <div style={S.cardHeader}>
        <div style={S.cardTitle}>Marketing &amp; Advertising Spend (QB)</div>
        <div style={S.cardSubtitle}>
          {fmtMoney(spend.total)} across {spend.by_vendor.length} vendor{spend.by_vendor.length === 1 ? "" : "s"}
          {spend.months_covered > 0 && ` · ${spend.months_covered}/${spend.periods_requested} month(s) snapshotted`}
        </div>
      </div>

      <div style={S.kpiGrid}>
        <Kpi label="Cost per Lead"        value={costPer.lead       != null ? fmtMoney2(costPer.lead)       : "—"} />
        <Kpi label="Cost per Sent Quote"  value={costPer.sent_quote != null ? fmtMoney2(costPer.sent_quote) : "—"} />
        <Kpi label="Cost per Won Quote"   value={costPer.won_quote  != null ? fmtMoney2(costPer.won_quote)  : "—"} />
        <Kpi label="Cost per Invoiced"    value={costPer.invoiced   != null ? fmtMoney2(costPer.invoiced)   : "—"} />
        <Kpi label="ROAS"                 value={roas != null ? `${roas}x` : "—"}
             highlight={roas >= 5 ? "good" : roas >= 2 ? "ok" : "bad"}
             sub="revenue ÷ ad spend" />
      </div>

      {spend.by_vendor.length > 0 && (
        <table style={{ ...S.table, marginTop: 12 }}>
          <thead><tr>
            <th style={{ ...S.th, textAlign: "left" }}>Vendor</th>
            <th style={S.th}>Spend</th>
            <th style={S.th}>% of total</th>
          </tr></thead>
          <tbody>
            {spend.by_vendor.map(v => (
              <tr key={v.vendor}>
                <td style={S.tdLabel}>{v.vendor}</td>
                <td style={S.td}>{fmtMoney(v.amount)}</td>
                <td style={S.tdMuted}>{spend.total > 0 ? Math.round((v.amount / spend.total) * 100) + "%" : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {spend.total === 0 && (
        <div style={S.emptyText}>
          No marketing spend pulled yet. Open the Coach page and "Refresh from QB" for the months in this range —
          the marketing breakdown is computed there and reused here. Make sure your QB advertising/marketing accounts
          are mapped to the "Marketing" bucket (Account Mapping in Coach).
        </div>
      )}
    </div>
  );
}

// ── Detail table with sticky footer totals ────────────────────────────────
function DetailTable({ rows, turnaround, shownColumns, toggleCol, sortBy, sortIndicator, footer }) {
  const colDefs = [
    { key: "quote_num",    label: "Quote #",     render: r => r.quote_num || "—" },
    { key: "job_num",      label: "Job #",       render: r => r.job_num   || "—" },
    { key: "invoice_num",  label: "Inv #",       render: r => r.invoice_num || "—" },
    { key: "client_name",  label: "Client",      render: r => r.client_name || "—" },
    { key: "title",        label: "Title",       render: r => r.title || "—" },
    { key: "service_type", label: "Service",     render: r => r.service_type || "—" },
    { key: "lead_source",  label: "Lead Source", render: r => r.lead_source || "—" },
    { key: "status",       label: "Status",      render: r => r.status || "—" },
    { key: "drafted_date", label: "Drafted",     render: r => fmtDate(r.drafted_date), align: "right" },
    { key: "sent_date",    label: "Sent",        render: r => fmtDate(r.sent_date),    align: "right" },
    { key: "approved_date",label: "Approved",    render: r => fmtDate(r.approved_date), align: "right" },
    { key: "archived_date",label: "Archived",    render: r => fmtDate(r.archived_date), align: "right" },
    { key: "converted_date",label:"Converted",   render: r => fmtDate(r.converted_date), align: "right" },
    { key: "job_created",  label: "Job Started", render: r => fmtDate(r.job_created),  align: "right" },
    { key: "job_closed",   label: "Job Closed",  render: r => fmtDate(r.job_closed),   align: "right" },
    { key: "invoice_issued",label: "Invoiced",   render: r => fmtDate(r.invoice_issued), align: "right" },
    { key: "quote_total",  label: "Quote $",     render: r => fmtMoney(r.quote_total), align: "right" },
    { key: "job_revenue",  label: "Revenue",     render: r => fmtMoney(r.job_revenue), align: "right" },
    { key: "job_profit",   label: "GP",          render: r => fmtMoney(r.job_profit),  align: "right" },
    { key: "job_profit_pct",label:"GP%",         render: r => r.job_profit_pct != null ? `${Math.round(r.job_profit_pct)}%` : "—", align: "right" },
    { key: "days_request_to_invoice", label: "Days end-to-end",
      render: r => {
        if (!r._req_created || !r.invoice_issued) return "—";
        const d = Math.round((new Date(r.invoice_issued) - new Date(r._req_created)) / (24*60*60*1000));
        return d >= 0 ? `${d}d` : "—";
      }, align: "right" },
  ];
  const visibleCols = colDefs.filter(c => shownColumns[c.key]);

  return (
    <div style={S.card}>
      <div style={S.cardHeader}>
        <div>
          <div style={S.cardTitle}>Pipeline — {rows.length} row{rows.length === 1 ? "" : "s"}</div>
          {turnaround && (
            <div style={S.cardSubtitle}>
              Avg turnaround: drafted→sent {fmtDays(turnaround.drafted_to_sent)} ·
              sent→approved {fmtDays(turnaround.sent_to_approved)} ·
              approved→job {fmtDays(turnaround.approved_to_job)} ·
              job→close {fmtDays(turnaround.job_created_to_closed)} ·
              close→invoice {fmtDays(turnaround.closed_to_invoiced)} ·
              <strong style={{ color: "#a5ce3a" }}> end-to-end {fmtDays(turnaround.request_to_invoiced)}</strong>
            </div>
          )}
        </div>
        <details style={{ position: "relative" }}>
          <summary style={S.btn}>Columns ({visibleCols.length}/{colDefs.length})</summary>
          <div style={S.colsMenu}>
            {colDefs.map(c => (
              <label key={c.key} style={S.colsItem}>
                <input type="checkbox" checked={!!shownColumns[c.key]} onChange={() => toggleCol(c.key)} />
                {c.label}
              </label>
            ))}
          </div>
        </details>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={S.table}>
          <thead>
            <tr>
              {visibleCols.map(c => (
                <th key={c.key} onClick={() => sortBy(c.key)} style={{ ...S.th, textAlign: c.align || "left", cursor: "pointer" }}>
                  {c.label}{sortIndicator(c.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={visibleCols.length} style={{ ...S.tdMuted, textAlign: "center", padding: 24 }}>
                No matching rows. Try widening the date range or clearing the keyword.
              </td></tr>
            )}
            {rows.map((r, i) => (
              <tr key={i}>
                {visibleCols.map(c => (
                  <td key={c.key} style={{ ...(c.align === "right" ? S.td : S.tdLabel), textAlign: c.align || "left" }}>
                    {c.render(r)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {footer && rows.length > 0 && (
            <tfoot>
              <tr style={S.footerRow}>
                {visibleCols.map(c => {
                  let v = "";
                  if (c.key === "client_name") v = `${footer.rows} row${footer.rows === 1 ? "" : "s"}`;
                  else if (c.key === "quote_total")  v = `${fmtMoney(footer.quote_total)} (${footer.q_count})`;
                  else if (c.key === "job_revenue")  v = `${fmtMoney(footer.job_rev)} (${footer.j_count})`;
                  else if (c.key === "job_profit")   v = fmtMoney(footer.job_profit);
                  else if (c.key === "job_profit_pct") v = footer.profit_pct != null ? `${footer.profit_pct}%` : "—";
                  else if (c.key === "invoice_issued") v = `${footer.invoiced} invoiced`;
                  return <td key={c.key} style={{ ...(c.align === "right" ? S.td : S.tdLabel), textAlign: c.align || "left", fontWeight: 700, borderTop: "2px solid rgba(165,206,58,0.3)" }}>{v}</td>;
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

// ── KPI tile (mirrors Coach.jsx) ──────────────────────────────────────────
function Kpi({ label, value, sub, highlight }) {
  const border = highlight === "good" ? "rgba(34,197,94,0.4)"
                : highlight === "bad"  ? "rgba(239,68,68,0.4)"
                : highlight === "ok"   ? "rgba(245,158,11,0.4)"
                : "rgba(255,255,255,0.08)";
  return (
    <div style={{ ...S.kpiCard, borderColor: border }}>
      <div style={S.kpiLabel}>{label}</div>
      <div style={S.kpiValue}>{value}</div>
      {sub && <div style={S.kpiSub}>{sub}</div>}
    </div>
  );
}

// ── Styles (consistent with Coach.jsx) ────────────────────────────────────
const S = {
  page: { padding: 24, color: "#e8eaf0", fontFamily: "'Roboto', sans-serif", maxWidth: 1600, margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 12 },
  title: { fontSize: 28, fontWeight: 700, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.02em" },
  subtitle: { fontSize: 13, color: "#8892a4", marginTop: 4 },
  headerActions: { display: "flex", gap: 8 },

  filterBar: { display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 16, flexWrap: "wrap", padding: 12, background: "#161a20", borderRadius: 12, border: "1px solid rgba(255,255,255,0.06)" },
  input: { padding: "8px 10px", background: "#1e2328", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, color: "#f0f0f0", fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" },

  btn: { padding: "8px 14px", background: "#1e2328", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6, color: "#f0f0f0", fontSize: 13, cursor: "pointer", fontFamily: "inherit" },
  btnPrimary: { padding: "8px 14px", background: "#a5ce3a", border: "none", borderRadius: 6, color: "#0d0f12", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  chip: { padding: "8px 12px", background: "#1e2328", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, color: "#8892a4", fontSize: 11, cursor: "pointer", fontFamily: "inherit" },

  errorBanner: { background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", color: "#fca5a5", padding: "10px 14px", borderRadius: 8, marginBottom: 12, fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" },
  statusBanner: { background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)", color: "#86efac", padding: "10px 14px", borderRadius: 8, marginBottom: 12, fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" },
  bannerClose: { background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: 0, marginLeft: 12 },

  card: { background: "#161a20", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 16, marginBottom: 16 },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, gap: 12, flexWrap: "wrap" },
  cardTitle: { fontSize: 14, fontWeight: 700, color: "#e8eaf0", letterSpacing: "0.01em" },
  cardSubtitle: { fontSize: 12, color: "#8892a4", marginTop: 4 },
  emptyText: { fontSize: 13, color: "#8892a4", padding: 16, textAlign: "center" },

  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 16 },
  kpiCard: { background: "#161a20", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: 14 },
  kpiLabel: { fontSize: 11, color: "#8892a4", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 },
  kpiValue: { fontSize: 22, fontWeight: 800, color: "#f0f0f0" },
  kpiSub: { fontSize: 11, color: "#8892a4", marginTop: 4 },

  table: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  th: { padding: "8px 10px", color: "#8892a4", fontWeight: 600, fontSize: 11, textTransform: "uppercase", borderBottom: "1px solid rgba(255,255,255,0.08)", whiteSpace: "nowrap", userSelect: "none" },
  td: { padding: "8px 10px", color: "#f0f0f0", borderBottom: "1px solid rgba(255,255,255,0.04)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  tdMuted: { padding: "8px 10px", color: "#8892a4", borderBottom: "1px solid rgba(255,255,255,0.04)", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  tdLabel: { padding: "8px 10px", color: "#e8eaf0", borderBottom: "1px solid rgba(255,255,255,0.04)" },
  footerRow: { background: "rgba(165,206,58,0.04)" },

  colsMenu: { position: "absolute", right: 0, top: "calc(100% + 4px)", background: "#1e2328", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6, padding: 8, minWidth: 200, zIndex: 10, maxHeight: 320, overflowY: "auto" },
  colsItem: { display: "flex", alignItems: "center", gap: 8, padding: "4px 8px", fontSize: 12, cursor: "pointer", color: "#e8eaf0" },

  // Tab strip
  tabStrip: { display: "flex", gap: 4, marginBottom: 16, borderBottom: "1px solid rgba(255,255,255,0.08)" },
  tab: { padding: "10px 18px", background: "transparent", border: "none", borderBottom: "2px solid transparent", color: "#8892a4", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", letterSpacing: "0.02em" },
  tabActive: { color: "#a5ce3a", borderBottom: "2px solid #a5ce3a" },

  // Reviews tab
  reviewCard: { background: "#161a20", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 16, marginBottom: 12 },
  reviewHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 8, flexWrap: "wrap" },
  reviewMeta: { display: "flex", gap: 10, alignItems: "center", fontSize: 12, color: "#8892a4", flexWrap: "wrap" },
  reviewName: { fontSize: 14, fontWeight: 700, color: "#e8eaf0" },
  reviewBody: { fontSize: 13, color: "#cdd2dd", lineHeight: 1.5, marginBottom: 10, whiteSpace: "pre-wrap" },
  badge: { display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase" },
  badgeGood: { background: "rgba(34,197,94,0.15)", color: "#86efac", border: "1px solid rgba(34,197,94,0.3)" },
  badgeWarn: { background: "rgba(245,158,11,0.15)", color: "#fcd34d", border: "1px solid rgba(245,158,11,0.3)" },
  badgeMuted: { background: "rgba(255,255,255,0.05)", color: "#8892a4", border: "1px solid rgba(255,255,255,0.08)" },
  badgeAccent: { background: "rgba(165,206,58,0.12)", color: "#a5ce3a", border: "1px solid rgba(165,206,58,0.3)" },
  searchWrap: { position: "relative", maxWidth: 420 },
  searchDropdown: { position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "#1e2328", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6, maxHeight: 280, overflowY: "auto", zIndex: 20, boxShadow: "0 4px 16px rgba(0,0,0,0.4)" },
  searchOption: { padding: "8px 10px", fontSize: 13, color: "#e8eaf0", cursor: "pointer", borderBottom: "1px solid rgba(255,255,255,0.04)" },
  suggestPill: { display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", background: "rgba(165,206,58,0.12)", border: "1px solid rgba(165,206,58,0.4)", borderRadius: 6, color: "#a5ce3a", fontSize: 12, cursor: "pointer", fontWeight: 600 },
  techChip: { display: "inline-block", padding: "2px 8px", background: "rgba(165,206,58,0.08)", border: "1px solid rgba(165,206,58,0.2)", borderRadius: 4, color: "#cdd2dd", fontSize: 11, marginRight: 4 },
};

// ═══════════════════════════════════════════════════════════════════════════
// Reviews tab
// ═══════════════════════════════════════════════════════════════════════════

// Supabase REST silently caps at 1000 rows. Paginate anything that may exceed.
async function fetchAll(builder, pageSize = 1000) {
  const all = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await builder().range(from, from + pageSize - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;
    all.push(...data);
    if (data.length < pageSize) break;
    if (all.length >= 50000) break; // safety cap
  }
  return all;
}

function normName(s) {
  if (!s) return "";
  return s.toLowerCase().replace(/[^a-z\s,]/g, " ").replace(/\s+/g, " ").trim();
}

// Resolve techs/job for a (review, client) pair using the user's rules:
//   1 job → that job's techs
//   multi-job, rating ≥ 4 → union across all of the client's jobs
//   multi-job, rating ≤ 3 → only the closest job before the review date
function resolveAssignment(review, clientJobs, techsByJob) {
  if (!clientJobs.length) return { assigned_job: null, assigned_techs: [] };
  if (clientJobs.length === 1) {
    const j = clientJobs[0];
    return {
      assigned_job: j.job_num != null ? String(j.job_num) : null,
      assigned_techs: [...(techsByJob.get(String(j.job_num)) || [])],
    };
  }
  const rating = Number(review.rating ?? 5);
  const reviewDate = review.created_at ? new Date(review.created_at) : null;
  const isBad = rating <= 3;

  if (isBad && reviewDate) {
    const before = clientJobs
      .filter(j => j.closed_date && new Date(j.closed_date) <= reviewDate)
      .sort((a, b) => new Date(b.closed_date) - new Date(a.closed_date));
    const closest = before[0]
      || [...clientJobs].sort((a, b) => new Date(b.closed_date || 0) - new Date(a.closed_date || 0))[0];
    return {
      assigned_job: closest.job_num != null ? String(closest.job_num) : null,
      assigned_techs: [...(techsByJob.get(String(closest.job_num)) || [])],
    };
  }
  // Good multi-job → union
  const techSet = new Set();
  for (const j of clientJobs) {
    const t = techsByJob.get(String(j.job_num));
    if (t) for (const x of t) techSet.add(x);
  }
  return { assigned_job: null, assigned_techs: [...techSet] };
}

function ReviewsTab() {
  const [reviews, setReviews]       = useState([]);
  const [jobs, setJobs]             = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [filter, setFilter]         = useState("unassigned"); // unassigned | linked | assigned | all
  const [savingId, setSavingId]     = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [r, j, t] = await Promise.all([
          fetchAll(() => supabase.from("jobber_reviews").select("*").order("created_at", { ascending: false })),
          fetchAll(() => supabase.from("jb_jobs").select("job_num,client_id,client_name,closed_date,title").order("id", { ascending: false })),
          fetchAll(() => supabase.from("jobber_timesheets").select("job_number,user_name")),
        ]);
        setReviews(r); setJobs(j); setTimesheets(t);
      } catch (e) { setError(e.message); }
      finally { setLoading(false); }
    })();
  }, []);

  // job_num → Set<user_name>
  const techsByJob = useMemo(() => {
    const m = new Map();
    for (const ts of timesheets) {
      if (!ts.job_number || !ts.user_name) continue;
      const k = String(ts.job_number);
      if (!m.has(k)) m.set(k, new Set());
      m.get(k).add(ts.user_name);
    }
    return m;
  }, [timesheets]);

  // client_id → [jobs] (sorted newest closed first)
  const jobsByClientId = useMemo(() => {
    const m = new Map();
    for (const j of jobs) {
      if (!j.client_id) continue;
      const k = String(j.client_id);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(j);
    }
    for (const list of m.values()) {
      list.sort((a, b) => new Date(b.closed_date || 0) - new Date(a.closed_date || 0));
    }
    return m;
  }, [jobs]);

  // Distinct (id, name) for the searchbar
  const clients = useMemo(() => {
    const seen = new Map();
    for (const j of jobs) {
      if (!j.client_id || !j.client_name) continue;
      const id = String(j.client_id);
      if (!seen.has(id)) seen.set(id, { id, name: j.client_name, jobCount: 0 });
      seen.get(id).jobCount += 1;
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [jobs]);

  // Auto-suggest: clients whose name closely matches the reviewer name.
  // Returns { client, confidence } or null.
  const suggestClient = useCallback((reviewerName) => {
    if (!reviewerName) return null;
    const rn = normName(reviewerName);
    if (!rn) return null;

    // Exact match
    const exact = clients.find(c => normName(c.name) === rn);
    if (exact) return { client: exact, confidence: "exact" };

    // "Last, First" → "First Last"
    if (rn.includes(",")) {
      const flipped = rn.split(",").map(s => s.trim()).reverse().join(" ").replace(/\s+/g, " ").trim();
      const m = clients.find(c => normName(c.name) === flipped);
      if (m) return { client: m, confidence: "exact" };
    }

    // All multi-char words from reviewer appear in exactly one client
    const rWords = rn.split(/\s+/).filter(w => w.length >= 3);
    if (rWords.length >= 2) {
      const matches = clients.filter(c => {
        const cn = normName(c.name);
        return rWords.every(w => cn.includes(w));
      });
      if (matches.length === 1) return { client: matches[0], confidence: "strong" };
    }
    return null;
  }, [clients]);

  // Per-row display state
  const displayRows = useMemo(() => {
    return reviews.map(r => {
      const isManuallyAssigned = !!r.assigned_client_id;
      const isJobberLinked     = !isManuallyAssigned && !!r.job_number;

      let status, clientId, clientName, creditedJobs, creditedTechs;

      if (isManuallyAssigned) {
        status = "assigned";
        clientId = String(r.assigned_client_id);
        const cl = clients.find(c => c.id === clientId);
        clientName = cl?.name || "(unknown client)";
        creditedTechs = Array.isArray(r.assigned_techs) ? r.assigned_techs : [];
        if (r.assigned_job) {
          const j = jobs.find(j => String(j.job_num) === String(r.assigned_job));
          creditedJobs = j ? [j] : [{ job_num: r.assigned_job, title: "(job not found)" }];
        } else {
          // Multi-job credit (good multi-job rule)
          creditedJobs = jobsByClientId.get(clientId) || [];
        }
      } else if (isJobberLinked) {
        status = "linked";
        // Honour assigned_job override even when assigned_client_id wasn't set
        // (set-job-number patches job_number/assigned_job directly without
        // touching assigned_client_id). Otherwise the header would keep
        // showing the original job's client even after the link was changed.
        const effectiveJobNum = r.assigned_job || r.job_number;
        const j = jobs.find(j => String(j.job_num) === String(effectiveJobNum));
        clientId = j?.client_id ? String(j.client_id) : null;
        clientName = j?.client_name || r.reviewer_name || "(unknown client)";
        creditedJobs = j ? [j] : [{ job_num: effectiveJobNum, title: "(job not found)" }];
        creditedTechs = j ? [...(techsByJob.get(String(j.job_num)) || [])] : [];
      } else {
        status = "unassigned";
        clientId = null;
        clientName = null;
        creditedJobs = [];
        creditedTechs = [];
      }

      const suggestion = (status === "unassigned") ? suggestClient(r.reviewer_name) : null;
      return { review: r, status, clientId, clientName, creditedJobs, creditedTechs, suggestion };
    });
  }, [reviews, clients, jobs, jobsByClientId, techsByJob, suggestClient]);

  const filteredRows = useMemo(() => {
    if (filter === "all") return displayRows;
    return displayRows.filter(r => r.status === filter);
  }, [displayRows, filter]);

  const counts = useMemo(() => ({
    all:        displayRows.length,
    unassigned: displayRows.filter(r => r.status === "unassigned").length,
    linked:     displayRows.filter(r => r.status === "linked").length,
    assigned:   displayRows.filter(r => r.status === "assigned").length,
  }), [displayRows]);

  const assignClient = useCallback(async (reviewId, clientId) => {
    const review = reviews.find(r => r.id === reviewId);
    if (!review) return;
    const clientJobs = jobsByClientId.get(String(clientId)) || [];
    const resolved = resolveAssignment(review, clientJobs, techsByJob);
    setSavingId(reviewId);
    try {
      const { error: e } = await supabase
        .from("jobber_reviews")
        .update({
          assigned_client_id: String(clientId),
          assigned_job:       resolved.assigned_job,
          assigned_techs:     resolved.assigned_techs,
        })
        .eq("id", reviewId);
      if (e) throw e;
      setReviews(prev => prev.map(r => r.id === reviewId
        ? { ...r, assigned_client_id: String(clientId), assigned_job: resolved.assigned_job, assigned_techs: resolved.assigned_techs }
        : r));
    } catch (err) {
      alert(`Save failed: ${err.message}`);
    } finally {
      setSavingId(null);
    }
  }, [reviews, jobsByClientId, techsByJob]);

  const unassign = useCallback(async (reviewId) => {
    setSavingId(reviewId);
    try {
      const { error: e } = await supabase
        .from("jobber_reviews")
        .update({ assigned_client_id: null, assigned_job: null, assigned_techs: null })
        .eq("id", reviewId);
      if (e) throw e;
      setReviews(prev => prev.map(r => r.id === reviewId
        ? { ...r, assigned_client_id: null, assigned_job: null, assigned_techs: null }
        : r));
    } catch (err) {
      alert(`Unassign failed: ${err.message}`);
    } finally {
      setSavingId(null);
    }
  }, []);

  if (loading) return <div style={S.card}><div style={S.emptyText}>Loading reviews…</div></div>;
  if (error)   return <div style={S.errorBanner}>{error}</div>;

  return (
    <>
      <div style={{ ...S.filterBar, justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {[
            { key: "unassigned", label: `Needs review (${counts.unassigned})` },
            { key: "linked",     label: `Jobber-linked (${counts.linked})` },
            { key: "assigned",   label: `Manually assigned (${counts.assigned})` },
            { key: "all",        label: `All (${counts.all})` },
          ].map(t => (
            <button key={t.key}
              onClick={() => setFilter(t.key)}
              style={{
                ...S.chip,
                ...(filter === t.key
                  ? { background: "rgba(165,206,58,0.12)", borderColor: "#a5ce3a", color: "#a5ce3a" }
                  : {}),
              }}>{t.label}</button>
          ))}
        </div>
        <div style={{ fontSize: 11, color: "#8892a4" }}>
          {clients.length.toLocaleString()} clients · {reviews.length.toLocaleString()} reviews
        </div>
      </div>

      {filteredRows.length === 0 ? (
        <div style={S.card}><div style={S.emptyText}>No reviews match this filter.</div></div>
      ) : filteredRows.map(row => (
        <ReviewCard
          key={row.review.id}
          row={row}
          clients={clients}
          jobsByClientId={jobsByClientId}
          techsByJob={techsByJob}
          saving={savingId === row.review.id}
          onAssign={(clientId) => assignClient(row.review.id, clientId)}
          onUnassign={() => unassign(row.review.id)}
        />
      ))}
    </>
  );
}

// ── Single review row ─────────────────────────────────────────────────────
function ReviewCard({ row, clients, jobsByClientId, techsByJob, saving, onAssign, onUnassign }) {
  const { review, status, clientName, creditedJobs, creditedTechs, suggestion } = row;
  const [editing, setEditing] = useState(false);
  const rating = Number(review.rating || 0);
  const stars  = "★".repeat(Math.round(rating)) + "☆".repeat(Math.max(0, 5 - Math.round(rating)));
  const dateStr = review.created_at ? new Date(review.created_at).toLocaleDateString("en-CA") : "—";

  const showSearchbar = status === "unassigned" || editing;
  const ratingColor = rating >= 4 ? "#86efac" : rating <= 3 ? "#fca5a5" : "#fcd34d";

  return (
    <div style={S.reviewCard}>
      <div style={S.reviewHeader}>
        <div>
          <div style={S.reviewName}>{review.reviewer_name || "(anonymous)"}</div>
          <div style={S.reviewMeta}>
            <span style={{ color: ratingColor, fontSize: 14, letterSpacing: 1 }}>{stars}</span>
            <span>{rating}/5</span>
            <span>·</span>
            <span>{dateStr}</span>
            {status === "linked"     && <span style={{ ...S.badge, ...S.badgeAccent }}>Jobber-linked</span>}
            {status === "assigned"   && <span style={{ ...S.badge, ...S.badgeGood }}>Assigned</span>}
            {status === "unassigned" && <span style={{ ...S.badge, ...S.badgeWarn }}>Needs client</span>}
          </div>
        </div>
        {!showSearchbar && (
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={() => setEditing(true)} disabled={saving} style={S.btn}>Reassign</button>
            {status === "assigned" && (
              <button onClick={onUnassign} disabled={saving} style={S.btn}>Clear</button>
            )}
          </div>
        )}
      </div>

      {review.comment && <div style={S.reviewBody}>{review.comment}</div>}

      {/* Currently-credited info */}
      {!showSearchbar && (
        <div style={{ fontSize: 12, color: "#8892a4", marginBottom: 8 }}>
          <div><strong style={{ color: "#cdd2dd" }}>Client:</strong> {clientName || "—"}</div>
          {creditedJobs.length > 0 && (
            <div style={{ marginTop: 4 }}>
              <strong style={{ color: "#cdd2dd" }}>Job{creditedJobs.length > 1 ? "s" : ""} credited:</strong>{" "}
              {creditedJobs.map(j => `#${j.job_num}${j.title ? ` (${j.title})` : ""}`).join(", ")}
            </div>
          )}
          {creditedTechs.length > 0 && (
            <div style={{ marginTop: 4 }}>
              <strong style={{ color: "#cdd2dd" }}>Techs credited:</strong>{" "}
              {creditedTechs.map(t => <span key={t} style={S.techChip}>{t}</span>)}
            </div>
          )}
          {creditedTechs.length === 0 && status !== "unassigned" && (
            <div style={{ marginTop: 4, color: "#fcd34d" }}>
              No techs found on timesheets for the credited job(s).
            </div>
          )}
        </div>
      )}

      {/* Searchbar for unassigned (or when reassigning) */}
      {showSearchbar && (
        <ClientSearchbar
          clients={clients}
          suggestion={suggestion}
          jobsByClientId={jobsByClientId}
          techsByJob={techsByJob}
          review={review}
          saving={saving}
          onPick={async (id) => { await onAssign(id); setEditing(false); }}
          onCancel={editing ? () => setEditing(false) : null}
        />
      )}
    </div>
  );
}

// ── Client searchbar with auto-suggest ────────────────────────────────────
function ClientSearchbar({ clients, suggestion, jobsByClientId, techsByJob, review, saving, onPick, onCancel }) {
  const [query, setQuery] = useState("");
  const [open, setOpen]   = useState(false);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return clients
      .filter(c => c.name.toLowerCase().includes(q))
      .slice(0, 8);
  }, [clients, query]);

  // Pre-resolve what would be credited if the user accepts the suggestion
  const suggestedPreview = useMemo(() => {
    if (!suggestion) return null;
    const cj = jobsByClientId.get(String(suggestion.client.id)) || [];
    return resolveAssignment(review, cj, techsByJob);
  }, [suggestion, jobsByClientId, techsByJob, review]);

  return (
    <div>
      {suggestion && !query && (
        <div style={{ marginBottom: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: "#8892a4" }}>Suggested:</span>
          <button
            onClick={() => onPick(suggestion.client.id)}
            disabled={saving}
            style={S.suggestPill}
            title={suggestion.confidence === "exact" ? "Exact name match" : "Strong word-overlap match"}>
            ✓ {suggestion.client.name} ({suggestion.client.jobCount} job{suggestion.client.jobCount === 1 ? "" : "s"})
          </button>
          {suggestedPreview && suggestedPreview.assigned_techs.length > 0 && (
            <span style={{ fontSize: 11, color: "#8892a4" }}>
              → would credit {suggestedPreview.assigned_techs.join(", ")}
            </span>
          )}
          <span style={{ fontSize: 11, color: "#8892a4" }}>· or search:</span>
        </div>
      )}

      <div style={S.searchWrap}>
        <input
          type="text"
          placeholder="Search clients by name…"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          disabled={saving}
          style={{ ...S.input, width: "100%" }}
        />
        {open && matches.length > 0 && (
          <div style={S.searchDropdown}>
            {matches.map(c => (
              <div key={c.id}
                onMouseDown={() => onPick(c.id)}
                style={S.searchOption}>
                <div style={{ fontWeight: 600 }}>{c.name}</div>
                <div style={{ fontSize: 11, color: "#8892a4", marginTop: 2 }}>
                  {c.jobCount} job{c.jobCount === 1 ? "" : "s"}
                </div>
              </div>
            ))}
          </div>
        )}
        {open && query && matches.length === 0 && (
          <div style={{ ...S.searchDropdown, padding: 12, fontSize: 12, color: "#8892a4" }}>
            No clients match "{query}".
          </div>
        )}
      </div>

      {onCancel && (
        <div style={{ marginTop: 8 }}>
          <button onClick={onCancel} disabled={saving} style={S.btn}>Cancel</button>
        </div>
      )}
      {saving && <div style={{ marginTop: 8, fontSize: 11, color: "#a5ce3a" }}>Saving…</div>}
    </div>
  );
}
